import React, { useState, useEffect, useMemo } from "react";
import BotonManualHeader from "../../components/BotonManualHeader";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Alert,
  Spinner,
  Tabs,
  Tab,
  Pagination,
  Modal,
} from "react-bootstrap";
import {
  addDoc,
  collection,
  collectionGroup,
  doc,
  deleteDoc,
  getDoc,
  query,
  where,
  getFirestore,
  limit,
  serverTimestamp,
  setDoc,
  updateDoc,
} from "firebase/firestore";
// getDocs instrumentado: informa cuántas lecturas hace esta pantalla para
// poder atribuir el consumo de Firestore a cada usuario (solo superAdmin).
import { getDocs } from "../../functions/getDocsInstrumentado";
import { establecerOrigenConsumo } from "../../functions/consumoLecturas";
import { Line } from "react-chartjs-2";
import { Chart, registerables } from "chart.js";
import {
  format,
  isWithinInterval,
  startOfMonth,
  endOfMonth,
  differenceInDays,
} from "date-fns";
import { FaTrash, FaClipboardList } from "react-icons/fa"; // Asegúrate de tener este import
import firebaseApp from "../../firebase/credenciales";
import {
  FaFileExcel,
  FaFilter,
  FaCog,
  FaArrowUp,
  FaArrowDown,
  FaUndo,
  FaSearch,
  FaCheckSquare,
} from "react-icons/fa";
import Swal from "sweetalert2";
import * as XLSX from "xlsx"; // Asegúrate de instalar: npm install xlsx
import {
  cargarAlcanceGrupoAsistencia,
  filtrarPorDnisPermitidos,
} from "./groupScope";
import { logBusinessAction } from "../../services/AuditService";

// Registrar componentes necesarios para Chart.js
Chart.register(...registerables);

const db = getFirestore(firebaseApp);

const DEFAULT_COLUMNAS_REGISTROS = [
  { id: "fechaHora", label: "Fecha/Hora", visible: true, getValue: (r, fmt) => fmt(r) },
  { id: "fechaJornada", label: "Fecha Jornada", visible: true, getValue: (r) => r.fechaJornada || "—" },
  { id: "fechaMarcacion", label: "Hora", visible: true, getValue: (r) => r.fechaMarcacion || "—" },
  { id: "tipo", label: "Tipo", visible: true, getValue: (r) => r.tipo || "—" },
  { id: "dni", label: "DNI", visible: true, getValue: (r) => r.dni || "—" },
  { id: "nombreCompleto", label: "Nombre y Apellidos", visible: true, getValue: (r) => `${r.apellidos || ""} ${r.nombre || ""}`.trim() || "—" },
  { id: "cargo", label: "Cargo", visible: true, getValue: (r) => r.cargo || "—" },
  { id: "centro", label: "Centro", visible: true, getValue: (r) => r.centro?.centroNombre || r.centroNombre || "—" },
  { id: "origen", label: "Origen", visible: true, getValue: (r) => r.origen || "—" },
  { id: "validez", label: "Validez", visible: true, getValue: (r) => r.validacion?.esValida === true ? "Valido" : (r.validacion?.estado || "—") },
  { id: "geocerca", label: "Geocerca", visible: true, getValue: (r) => r.enGeocerca === true ? "SI" : "NO" },
  { id: "distanciaCentro", label: "Dist. Centro", visible: true, getValue: (r) => r.distanciaCentro ?? "—" },
  { id: "precision", label: "Precisión", visible: true, getValue: (r) => r.ubicacion?.precision ?? r.precision ?? "—" },
  { id: "deviceId", label: "Device ID", visible: false, getValue: (r) => r.metadata?.deviceId || "—" },
  { id: "modelName", label: "Modelo", visible: false, getValue: (r) => r.metadata?.modelName || "—" },
  { id: "modoAsistencia", label: "Modo", visible: false, getValue: (r) => r.metadata?.modoAsistencia || "—" },
  { id: "latCentro", label: "Lat Centro", visible: false, getValue: (r) => r.centro?.latitud ?? "—" },
  { id: "lngCentro", label: "Lng Centro", visible: false, getValue: (r) => r.centro?.longitud ?? "—" },
  { id: "latCaptura", label: "Lat Captura", visible: false, getValue: (r) => r.ubicacion?.latitud ?? "—" },
  { id: "lngCaptura", label: "Lng Captura", visible: false, getValue: (r) => r.ubicacion?.longitud ?? "—" },
  { id: "tipoTurno", label: "Tipo Turno", visible: false, getValue: (r) => r.tipoTurno || "—" },
  { id: "turnoDetectado", label: "Turno Detectado", visible: false, getValue: (r) => r.validacion?.contextoValidacion?.tipoTurnoDetectado || "—" },
  { id: "retroactivo", label: "Retroactivo", visible: false, getValue: (r) => r.esRegistroRetroactivo === true ? "SI" : "NO" },
  { id: "email", label: "Email", visible: false, getValue: (r) => r.email || "—" },
  { id: "ruc", label: "RUC", visible: false, getValue: (r) => r.ruc || "—" },
  { id: "so", label: "Sistema Operativo", visible: false, getValue: (r) => `${r?.metadata?.osName || ""} ${r?.metadata?.osVersion || ""}`.trim() || "—" },
  { id: "versionApp", label: "Versión App", visible: false, getValue: (r) => r.metadata?.version || "—" },
];

const getUserStorageKey = (usr) => {
  const userId = String(usr?.dni || usr?.id || usr?.uid || usr?.email || "default").trim();
  return `config_columnas_registros_aa_${userId}`;
};

const loadStoredColumnasRegistros = (usr) => {
  try {
    const key = getUserStorageKey(usr);
    const stored = localStorage.getItem(key) || localStorage.getItem("config_columnas_registros_aa");
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed) && parsed.length > 0) {
        const mapped = parsed
          .map((item) => {
            const found = DEFAULT_COLUMNAS_REGISTROS.find((c) => c.id === item.id);
            if (found) {
              return { ...found, visible: !!item.visible };
            }
            return null;
          })
          .filter(Boolean);

        DEFAULT_COLUMNAS_REGISTROS.forEach((defCol) => {
          if (!mapped.some((c) => c.id === defCol.id)) {
            mapped.push({ ...defCol });
          }
        });
        return mapped;
      }
    }
  } catch (e) {
    console.error("Error al cargar configuración de columnas de registros:", e);
  }
  return DEFAULT_COLUMNAS_REGISTROS.map((col) => ({ ...col }));
};

const normalizeTextToken = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, "_");
const isGlobalScopeRole = (role) => {
  const normalized = normalizeTextToken(role);
  return (
    normalized === "superadmin" ||
    normalized === "admin" ||
    normalized === "administrador"
  );
};

const ReportesAA = ({ usuario }) => {
  // Estados para filtros
  const [rucEmpresa, setRucEmpresa] = useState(usuario?.ruc || "");
  const [selectedMonth, setSelectedMonth] = useState(
    format(new Date(), "yyyy-MM")
  );
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);

  // Estados para datos
  const [attendanceData, setAttendanceData] = useState([]);
  const [absenceData, setAbsenceData] = useState([]);
  const [filteredAttendance, setFilteredAttendance] = useState([]);
  const [filteredAbsences, setFilteredAbsences] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [employeeList, setEmployeeList] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState("todos");
  const [editando, setEditando] = useState({}); // { [dni-dia]: { ingreso, salida } }
  const [tabActivo, setTabActivo] = useState("resumen");

  // Estados para estadísticas
  const [statistics, setStatistics] = useState({
    totalEmployees: 0,
    attendanceRate: 0,
    absenceRate: 0,
    lateArrivals: 0,
    absencesByType: {},
    attendanceByDay: {},
  });

  const [grupos, setGrupos] = useState([]);
  const [grupoSeleccionado, setGrupoSeleccionado] = useState("todos");
  const [alcanceGrupo, setAlcanceGrupo] = useState({
    restringidoPorGrupo: false,
    grupoAlcance: null,
  });
  const [dnisPermitidos, setDnisPermitidos] = useState(null);
  const [scopeLoading, setScopeLoading] = useState(true);
  const [gruposAsignadosUsuario, setGruposAsignadosUsuario] = useState([]);

  // Cargar alcance por grupo según el usuario autenticado
  useEffect(() => {
    const cargarScope = async () => {
      if (!rucEmpresa) return;
      setScopeLoading(true);
      try {
        const scope = await cargarAlcanceGrupoAsistencia({
          db,
          rucEmpresa,
          usuario,
        });

        setGrupos(scope.gruposDisponibles);
        setGrupoSeleccionado(scope.grupoFijoId || "todos");
        setDnisPermitidos(scope.dnisPermitidos);
        setAlcanceGrupo({
          restringidoPorGrupo: scope.restringidoPorGrupo,
          grupoAlcance: scope.grupoAlcance || null,
        });
        const esRolGlobal = isGlobalScopeRole(usuario?.rol);
        if (!esRolGlobal && usuario?.dni) {
          try {
            const [membershipDoc, gruposSnap] = await Promise.all([
              getDoc(doc(db, `grupos/${String(rucEmpresa)}/memberships`, String(usuario.dni).trim())),
              getDocs(collection(db, `grupos/${String(rucEmpresa)}/grupos`)),
            ]);
            const gruposEmpresa = gruposSnap.docs.map((groupDoc) => ({
              id: groupDoc.id,
              name: groupDoc.data()?.name || "Grupo sin nombre",
              members: Array.isArray(groupDoc.data()?.members) ? groupDoc.data().members : [],
            }));
            const membershipData = membershipDoc.exists() ? (membershipDoc.data() || {}) : {};
            const singleGroupId = String(membershipData?.groupId || "").trim();
            const multipleGroupIds = Array.isArray(membershipData?.groupIds)
              ? membershipData.groupIds.map((id) => String(id || "").trim()).filter(Boolean)
              : [];
            const groupIdsDesdeMembership = Array.from(
              new Set([singleGroupId, ...multipleGroupIds].filter(Boolean))
            );
            const dniStr = String(usuario.dni).trim();
            const groupIdsDesdeMembers = gruposEmpresa
              .filter((g) => (g.members || []).map((m) => String(m || "").trim()).includes(dniStr))
              .map((g) => String(g.id));
            const groupIds = Array.from(new Set([...groupIdsDesdeMembership, ...groupIdsDesdeMembers]));
            const gruposAsignados = gruposEmpresa.filter((g) => groupIds.includes(String(g.id)));

            setGruposAsignadosUsuario(gruposAsignados);
            if (gruposAsignados.length > 0) {
              setGrupos(gruposAsignados);
              setGrupoSeleccionado((prev) => {
                if (prev && gruposAsignados.some((g) => String(g.id) === String(prev))) return prev;
                return gruposAsignados[0].id;
              });
              const dnisUnion = new Set();
              gruposAsignados.forEach((g) => {
                (g.members || []).forEach((dni) => {
                  const dniNormalizado = String(dni || "").trim();
                  if (dniNormalizado) dnisUnion.add(dniNormalizado);
                });
              });
              setDnisPermitidos(dnisUnion);
            } else {
              setDnisPermitidos(new Set());
            }
          } catch (membershipError) {
            console.error("Error al resolver grupos asignados del usuario:", membershipError);
            setGruposAsignadosUsuario([]);
          }
        } else {
          setGruposAsignadosUsuario([]);
        }
        if (scope.restringidoPorGrupo && !scope.grupoAlcance) {
          setError("Tu usuario no tiene un grupo asignado para este módulo.");
        }
      } catch (e) {
        console.error("Error al cargar alcance de grupos:", e);
        setGrupos([]);
        setGrupoSeleccionado("todos");
        setDnisPermitidos(new Set());
        setGruposAsignadosUsuario([]);
      } finally {
        setScopeLoading(false);
      }
    };

    cargarScope();
  }, [rucEmpresa, usuario]);

  const lineChartOptions = {
    responsive: true,
    plugins: {
      legend: { display: false },
      title: { display: false },
    },
    scales: {
      y: {
        title: {
          display: true,
          text: "Número de asistencias",
        },
        beginAtZero: true,
        ticks: {
          stepSize: 1,
          precision: 0,
          callback: function (value) {
            return Number.isInteger(value) ? value : null;
          },
        },
      },
      x: {
        display: true,
        title: {
          display: true,
          text: "Día del mes",
        },
        ticks: {
          autoSkip: false,
        },
      },
    },
  };

  // Estado para controlar la hora de inicio laboral
  const [startHour, setStartHour] = useState(9); // Por defecto 9:00 AM
  const [startMinute, setStartMinute] = useState(0);

  // Nuevos estados para mejoras
  const [paginaActual, setPaginaActual] = useState(1);
  const [elementosPorPagina, setElementosPorPagina] = useState(10);
  const [filtroEstado, setFiltroEstado] = useState("todos");
  const [mostrarSoloConMarcacion, setMostrarSoloConMarcacion] = useState(false);
  const [busquedaEmpleado, setBusquedaEmpleado] = useState("");
  // Filtro por DNI en el selector de empleados (acelera encontrar a uno concreto)
  const [filtroDni, setFiltroDni] = useState("");
  // Lookup directo por DNI en Firestore: encuentra activos o inactivos sin
  // depender del switch "incluir inactivos" ni del mes seleccionado
  const [empleadosPorDni, setEmpleadosPorDni] = useState([]);
  const [dniBuscando, setDniBuscando] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [incluirInactivos, setIncluirInactivos] = useState(false);
  // Inactivos se cargan de forma perezosa (solo al activar el switch) para no
  // penalizar la carga principal con documentos que casi nunca se consultan
  const [empleadosInactivos, setEmpleadosInactivos] = useState([]);
  const [inactivosCargados, setInactivosCargados] = useState(false);
  const [inactivosLoading, setInactivosLoading] = useState(false);
  const [paginaRegistros, setPaginaRegistros] = useState(1);
  const [elementosPorPaginaRegistros, setElementosPorPaginaRegistros] =
    useState(25);
  const [busquedaRegistro, setBusquedaRegistro] = useState("");
  const [filtroTipoRegistro, setFiltroTipoRegistro] = useState("todos");
  const [vistaAvanzadaRegistros, setVistaAvanzadaRegistros] = useState(false);
  const [showModalColumnas, setShowModalColumnas] = useState(false);
  const [columnasRegistrosConfig, setColumnasRegistrosConfig] = useState(() => loadStoredColumnasRegistros(usuario));
  const [busquedaColumnaModal, setBusquedaColumnaModal] = useState("");

  // Sincronizar preferencias personalizadas por usuario en localStorage y Firestore
  useEffect(() => {
    const cargarPreferenciasUsuario = async () => {
      const userId = String(usuario?.dni || usuario?.id || usuario?.uid || "").trim();
      if (!userId) return;

      const key = getUserStorageKey(usuario);
      const localStored = localStorage.getItem(key);
      if (localStored) {
        setColumnasRegistrosConfig(loadStoredColumnasRegistros(usuario));
      }

      try {
        const prefRef = doc(db, "user_preferences", userId);
        const prefSnap = await getDoc(prefRef);
        if (prefSnap.exists() && prefSnap.data()?.columnasRegistrosConfig) {
          const firestoreCols = prefSnap.data().columnasRegistrosConfig;
          if (Array.isArray(firestoreCols) && firestoreCols.length > 0) {
            localStorage.setItem(key, JSON.stringify(firestoreCols));
            setColumnasRegistrosConfig(loadStoredColumnasRegistros(usuario));
          }
        }
      } catch (err) {
        console.warn("No se pudo cargar preferencias de columnas desde Firestore:", err);
      }
    };

    cargarPreferenciasUsuario();
  }, [usuario]);

  const guardarConfiguracionColumnas = async (newCols) => {
    setColumnasRegistrosConfig(newCols);
    const userId = String(usuario?.dni || usuario?.id || usuario?.uid || "").trim();
    const toSave = newCols.map(({ id, visible }) => ({ id, visible }));

    try {
      const key = getUserStorageKey(usuario);
      localStorage.setItem(key, JSON.stringify(toSave));
    } catch (e) {
      console.error("Error guardando columnas en localStorage:", e);
    }

    if (userId) {
      try {
        const prefRef = doc(db, "user_preferences", userId);
        await setDoc(prefRef, {
          columnasRegistrosConfig: toSave,
          dni: usuario?.dni || "",
          userEmail: usuario?.email || "",
          updatedAt: serverTimestamp(),
        }, { merge: true });
      } catch (err) {
        console.warn("Error respaldando preferencias de columnas en Firestore:", err);
      }
    }
  };

  const toggleColumna = (id) => {
    const newCols = columnasRegistrosConfig.map((col) =>
      col.id === id ? { ...col, visible: !col.visible } : col
    );
    guardarConfiguracionColumnas(newCols);
  };

  const moverColumna = (index, direction) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= columnasRegistrosConfig.length) return;
    const newCols = [...columnasRegistrosConfig];
    const temp = newCols[index];
    newCols[index] = newCols[targetIndex];
    newCols[targetIndex] = temp;
    guardarConfiguracionColumnas(newCols);
  };

  const aplicarPreajusteColumnas = (tipo) => {
    let newCols = [];
    if (tipo === "default") {
      newCols = DEFAULT_COLUMNAS_REGISTROS.map((col) => ({ ...col }));
    } else if (tipo === "todas" || tipo === "avanzada") {
      newCols = columnasRegistrosConfig.map((col) => ({ ...col, visible: true }));
    } else if (tipo === "basica") {
      const basicIds = [
        "fechaHora", "fechaJornada", "fechaMarcacion", "tipo", "dni",
        "nombreCompleto", "cargo", "centro", "origen", "validez",
        "geocerca", "distanciaCentro", "precision"
      ];
      newCols = columnasRegistrosConfig.map((col) => ({
        ...col,
        visible: basicIds.includes(col.id),
      }));
    }
    guardarConfiguracionColumnas(newCols);
  };

  // Devuelve los minutos de tardanza (0 si no hay tardanza)
  const getTardanzaMin = (ingreso, horario) => {
    if (!ingreso || !horario || ingreso === "—" || horario === "—") return null;
    const [h1, m1] = ingreso.split(":").map(Number);
    const [h2, m2] = horario.split(":").map(Number);
    const min = h1 * 60 + m1 - (h2 * 60 + m2);
    return min > 0 ? min : 0;
  };

  // Devuelve los minutos de diferencia de salida (positivo: posterior, negativo: temprana)
  const getSalidaMin = (salida, horario) => {
    if (!salida || !horario || salida === "—" || horario === "—") return null;
    const [h1, m1] = salida.split(":").map(Number);
    const [h2, m2] = horario.split(":").map(Number);
    return h1 * 60 + m1 - (h2 * 60 + m2);
  };

  useEffect(() => {
    if (!rucEmpresa || scopeLoading) {
      if (!scopeLoading) {
        setError("No se ha podido determinar la empresa");
        setInitialLoading(false);
      }
      return;
    }

    const initializeComponent = async () => {
      setInitialLoading(true);
      try {
        // Paralelizar: lista de empleados + datos de asistencia (son independientes)
        const [employees] = await Promise.all([
          getEmployeeList(),
          (async () => { await fetchData(); })(),
        ]);

        // Convertir a formato para el selector, asegurando centroNombre
        const uniqueEmployees = new Map();
        employees.forEach((employee) => {
          uniqueEmployees.set(employee.dni, {
            ...employee,
            dni: employee.dni,
            nombre:
              `${employee.apellidos || ""} ${employee.name || ""}`.trim() ||
              `${employee.name || ""} ${employee.apellidos || ""}`.trim(),
            centroNombre: employee.centroNombre || "",
          });
        });

        const employeeArr = Array.from(uniqueEmployees.values()).sort((a, b) =>
          a.nombre.localeCompare(b.nombre)
        );
        setAllEmployees(employeeArr);
        setEmployeeList(employeeArr.filter(e => e.activo === true));

        setInitialLoading(false);
      } catch (err) {
        console.error("Error al inicializar:", err);
        setError("Error al cargar datos iniciales");
        setInitialLoading(false);
      }
    };

    initializeComponent();
  }, [rucEmpresa, scopeLoading]);

  // Efecto para cargar inactivos de forma perezosa: solo cuando se activa el
  // switch (una sola vez). Mantiene la query principal liviana (solo activos).
  useEffect(() => {
    if (!incluirInactivos || inactivosCargados || inactivosLoading) return;
    if (!rucEmpresa || scopeLoading) return;

    let cancelado = false;
    const cargarInactivos = async () => {
      setInactivosLoading(true);
      try {
        const usersQuery = query(
          collection(db, "users"),
          where("rucEmpresa", "==", rucEmpresa),
          where("activo", "==", false)
        );
        const snapshot = await getDocs(usersQuery);
        const inactivos = [];
        snapshot.forEach((d) => {
          const data = d.data();
          inactivos.push({
            ...data,
            id: d.id,
            dni: data.dni,
            nombre:
              `${data.apellidos || ""} ${data.name || ""}`.trim() ||
              `${data.name || ""} ${data.apellidos || ""}`.trim(),
            centroNombre: data.centroNombre || "",
          });
        });
        if (!cancelado) {
          setEmpleadosInactivos(
            filtrarPorDnisPermitidos(inactivos, (u) => u?.dni, dnisPermitidos)
          );
          setInactivosCargados(true);
        }
      } catch (err) {
        console.error("Error al cargar empleados inactivos:", err);
      } finally {
        if (!cancelado) setInactivosLoading(false);
      }
    };
    cargarInactivos();
    return () => { cancelado = true; };
  }, [incluirInactivos, inactivosCargados, inactivosLoading, rucEmpresa, scopeLoading, dnisPermitidos]);

  // Efecto para actualizar la lista de empleados base a mostrar.
  // El switch de inactivos incluye a TODOS los inactivos de la empresa (no solo
  // a los con registros del mes): si el usuario busca a alguien cesado, debe
  // aparecer aunque su último registro sea de un mes anterior.
  useEffect(() => {
    if (!allEmployees || allEmployees.length === 0) return;

    let empleadosBase = allEmployees.filter(emp => emp.activo === true);

    if (incluirInactivos) {
      const inactivos = empleadosInactivos.filter(emp => emp.activo !== true);
      empleadosBase = [...empleadosBase, ...inactivos].sort((a, b) =>
        a.nombre.localeCompare(b.nombre)
      );
    }

    setEmployeeList(empleadosBase);
  }, [allEmployees, incluirInactivos, empleadosInactivos]);

  // Lookup directo por DNI (con debounce de 400ms): busca en users por prefijo
  // de DNI — encuentra activos o inactivos sin depender del switch ni del mes.
  // Usa el índice automático de campo simple en "dni" (sin índice compuesto).
  useEffect(() => {
    const dni = filtroDni.trim();
    if (dni.length < 4) {
      setEmpleadosPorDni([]);
      return;
    }
    let cancelado = false;
    const timer = setTimeout(async () => {
      setDniBuscando(true);
      try {
        const snap = await getDocs(
          query(
            collection(db, "users"),
            where("dni", ">=", dni),
            where("dni", "<=", dni + ""),
            limit(10)
          )
        );
        if (cancelado) return;
        const encontrados = [];
        snap.forEach((d) => {
          const data = d.data();
          // Restringir a la empresa del usuario (el query es por DNI global)
          if (rucEmpresa && data.rucEmpresa !== rucEmpresa) return;
          encontrados.push({
            ...data,
            id: d.id,
            dni: data.dni,
            nombre:
              `${data.apellidos || ""} ${data.name || ""}`.trim() ||
              `${data.name || ""} ${data.apellidos || ""}`.trim(),
            centroNombre: data.centroNombre || "",
            inactivo: data.activo !== true,
          });
        });
        setEmpleadosPorDni(
          filtrarPorDnisPermitidos(encontrados, (u) => u?.dni, dnisPermitidos)
        );
      } catch (err) {
        console.warn("Lookup por DNI falló:", err?.message);
        if (!cancelado) setEmpleadosPorDni([]);
      } finally {
        if (!cancelado) setDniBuscando(false);
      }
    }, 400);
    return () => {
      cancelado = true;
      clearTimeout(timer);
    };
  }, [filtroDni, rucEmpresa, dnisPermitidos]);

  // Lista del selector: filtrada por el texto del DNI + los resultados del
  // lookup directo que aún no estén en la lista (inactivos sin datos del mes)
  const empleadosSelector = useMemo(() => {
    const dniBuscado = filtroDni.trim();
    const base = dniBuscado
      ? employeeList.filter((emp) =>
          String(emp.dni || "").includes(dniBuscado)
        )
      : employeeList;
    if (empleadosPorDni.length === 0) return base;
    const dnisBase = new Set(base.map((e) => e.dni));
    const extras = empleadosPorDni.filter((e) => !dnisBase.has(e.dni));
    return extras.length ? [...base, ...extras] : base;
  }, [employeeList, filtroDni, empleadosPorDni]);

  // DNIs que coinciden con el filtro por DNI (null = sin filtro activo).
  // Fuente única para acotar los datos Y las filas de todas las pestañas.
  const dnisFiltroDni = useMemo(() => {
    const dniBuscado = filtroDni.trim();
    if (!dniBuscado) return null;
    return new Set([
      ...empleadosSelector.map((emp) => String(emp.dni)),
      ...empleadosPorDni
        .filter((emp) => String(emp.dni).includes(dniBuscado))
        .map((emp) => String(emp.dni)),
    ]);
  }, [filtroDni, empleadosSelector, empleadosPorDni]);

  // Si el DNI escrito corresponde a un único empleado, seleccionarlo solo:
  // el usuario escribió el DNI para ver a esa persona, no para elegir en un combo
  useEffect(() => {
    const dni = filtroDni.trim();
    if (dni.length !== 8 || empleadosPorDni.length !== 1) return;
    const unico = empleadosPorDni[0];
    if (String(unico.dni) === dni) setSelectedEmployee(unico.dni);
  }, [filtroDni, empleadosPorDni]);

  // Si el empleado seleccionado queda fuera del filtro de DNI (y del lookup),
  // volver a "todos" para que el selector no quede vacío/inconsistente
  useEffect(() => {
    if (selectedEmployee === "todos") return;
    const dnisVisibles = new Set([
      ...empleadosSelector.map((emp) => emp.dni),
      ...empleadosPorDni.map((emp) => emp.dni),
    ]);
    if (!dnisVisibles.has(selectedEmployee)) setSelectedEmployee("todos");
  }, [empleadosSelector, empleadosPorDni, selectedEmployee]);

  // Efecto para filtrar datos cuando cambia el empleado seleccionado
  useEffect(() => {
    let empleadosFiltrados = employeeList;
    if (grupoSeleccionado !== "todos") {
      const grupo = grupos.find((g) => g.id === grupoSeleccionado);
      if (grupo) {
        empleadosFiltrados = employeeList.filter((emp) =>
          grupo.members.includes(emp.dni)
        );
      }
    }

    // Filtro por DNI: acota los datos a los trabajadores cuyo DNI coincide con
    // lo escrito, en TODAS las pestañas (resumen, registros y detalle de horas),
    // sin necesidad de elegir a la persona en el selector
    if (dnisFiltroDni && selectedEmployee === "todos") {
      const grupoActual =
        grupoSeleccionado !== "todos"
          ? grupos.find((g) => g.id === grupoSeleccionado)
          : null;

      const coincide = (record) => {
        const dni = String(record.dni);
        if (!dnisFiltroDni.has(dni)) return false;
        if (grupoActual && !grupoActual.members.includes(dni)) return false;
        return true;
      };

      setFilteredAttendance(attendanceData.filter(coincide));
      setFilteredAbsences(absenceData.filter(coincide));
      setPaginaActual(1);
      return;
    }

    if (selectedEmployee === "todos") {
      setFilteredAttendance(
        attendanceData.filter((record) =>
          empleadosFiltrados.some((emp) => emp.dni === record.dni)
        )
      );
      setFilteredAbsences(
        absenceData.filter((record) =>
          empleadosFiltrados.some((emp) => emp.dni === record.dni)
        )
      );
    } else {
      setFilteredAttendance(
        attendanceData.filter((record) => record.dni === selectedEmployee)
      );
      setFilteredAbsences(
        absenceData.filter((record) => record.dni === selectedEmployee)
      );
    }
    setPaginaActual(1);
  }, [
    selectedEmployee,
    attendanceData,
    absenceData,
    grupoSeleccionado,
    grupos,
    employeeList,
    dnisFiltroDni,
  ]);

  // Efecto para calcular estadísticas cuando cambian los datos filtrados
  useEffect(() => {
    calculateStatistics();
  }, [filteredAttendance, filteredAbsences, startHour, startMinute]);

  useEffect(() => {
    setPaginaRegistros(1);
  }, [busquedaRegistro, filtroTipoRegistro, selectedMonth, selectedEmployee, grupoSeleccionado, vistaAvanzadaRegistros, filtroDni]);

  // Función para cargar datos - Optimizada

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      // Marca el origen de las lecturas de esta pantalla para el seguimiento
      // de consumo por usuario (visible solo para superAdmin).
      establecerOrigenConsumo("asistencia:reportes");
      const chunkValues = (values, size = 10) => {
        const chunks = [];
        for (let i = 0; i < values.length; i += size) {
          chunks.push(values.slice(i, i + size));
        }
        return chunks;
      };
      const dnisScope =
        dnisPermitidos instanceof Set ? Array.from(dnisPermitidos) : null;
      if (dnisScope && dnisScope.length === 0) {
        setAttendanceData([]);
        setAbsenceData([]);
        setFilteredAttendance([]);
        setFilteredAbsences([]);
        return;
      }

      // Obtener el rango de fechas para el mes seleccionado
      const [year, month] = selectedMonth.split("-");
      const fechaInicio = `${year}-${month.padStart(2, "0")}-01`;
      const fechaFin = `${year}-${month.padStart(2, "0")}-${new Date(
        year,
        month,
        0
      )
        .getDate()
        .toString()
        .padStart(2, "0")}`;

      const attendanceRecords = [];
      if (dnisScope && dnisScope.length > 0) {
        try {
          const chunks = chunkValues(dnisScope, 10);
          const snaps = await Promise.all(
            chunks.map((chunk) =>
              getDocs(
                query(
                  collectionGroup(db, "registros"),
                  where("ruc", "==", rucEmpresa),
                  where("dni", "in", chunk),
                  where("fechaJornada", ">=", fechaInicio),
                  where("fechaJornada", "<=", fechaFin)
                )
              )
            )
          );
          snaps.forEach((snap) =>
            snap.forEach((doc) => attendanceRecords.push({ id: doc.id, ...doc.data() }))
          );
        } catch (scopeQueryError) {
          console.warn("Fallback a consulta general de asistencia:", scopeQueryError?.message);
          const asistenciaQuery = query(
            collectionGroup(db, "registros"),
            where("ruc", "==", rucEmpresa),
            where("fechaJornada", ">=", fechaInicio),
            where("fechaJornada", "<=", fechaFin)
          );
          const asistenciaSnapshot = await getDocs(asistenciaQuery);
          asistenciaSnapshot.forEach((doc) => {
            attendanceRecords.push({ id: doc.id, ...doc.data() });
          });
        }
      } else {
        // Usar collectionGroup para obtener registros de asistencia de todos los empleados de la empresa en el mes
        const asistenciaQuery = query(
          collectionGroup(db, "registros"),
          where("ruc", "==", rucEmpresa),
          where("fechaJornada", ">=", fechaInicio),
          where("fechaJornada", "<=", fechaFin)
        );
        const asistenciaSnapshot = await getDocs(asistenciaQuery);
        asistenciaSnapshot.forEach((doc) => {
          attendanceRecords.push({ id: doc.id, ...doc.data() });
        });
      }
      const attendanceRecordsFiltrados = filtrarPorDnisPermitidos(
        attendanceRecords,
        (record) => record?.dni,
        dnisPermitidos
      );

      const absenceRecords = [];
      const fechaInicioFiltro = new Date(year, month - 1, 1);
      if (dnisScope && dnisScope.length > 0) {
        try {
          const chunks = chunkValues(dnisScope, 10);
          const snaps = await Promise.all(
            chunks.map((chunk) =>
              getDocs(
                query(
                  collectionGroup(db, "ausencias"),
                  where("ruc", "==", rucEmpresa),
                  where("dni", "in", chunk),
                  where("fechaInicio", "<=", new Date(year, month, 0))
                )
              )
            )
          );
          snaps.forEach((snap) => {
            snap.forEach((doc) => {
              const data = doc.data();
              if (data.fechaFin && data.fechaFin.toDate() >= fechaInicioFiltro) {
                absenceRecords.push({ id: doc.id, ...data });
              }
            });
          });
        } catch (scopeQueryError) {
          console.warn("Fallback a consulta general de ausencias:", scopeQueryError?.message);
          const ausenciasQuery = query(
            collectionGroup(db, "ausencias"),
            where("ruc", "==", rucEmpresa),
            where("fechaInicio", "<=", new Date(year, month, 0))
          );
          const ausenciasSnapshot = await getDocs(ausenciasQuery);
          ausenciasSnapshot.forEach((doc) => {
            const data = doc.data();
            if (data.fechaFin && data.fechaFin.toDate() >= fechaInicioFiltro) {
              absenceRecords.push({ id: doc.id, ...data });
            }
          });
        }
      } else {
        // Cargar ausencias usando collectionGroup también
        const ausenciasQuery = query(
          collectionGroup(db, "ausencias"),
          where("ruc", "==", rucEmpresa),
          where("fechaInicio", "<=", new Date(year, month, 0))
        );
        const ausenciasSnapshot = await getDocs(ausenciasQuery);
        ausenciasSnapshot.forEach((doc) => {
          const data = doc.data();
          // Filtrar en JS por fechaFin
          if (data.fechaFin && data.fechaFin.toDate() >= fechaInicioFiltro) {
            absenceRecords.push({ id: doc.id, ...data });
          }
        });
      }
      const absenceRecordsFiltrados = filtrarPorDnisPermitidos(
        absenceRecords,
        (record) => record?.dni,
        dnisPermitidos
      );

      setAttendanceData(attendanceRecordsFiltrados);
      setAbsenceData(absenceRecordsFiltrados);
      setFilteredAttendance(attendanceRecordsFiltrados);
      setFilteredAbsences(absenceRecordsFiltrados);
    } catch (err) {
      console.error("Error al cargar datos:", err);
      setError("Error al cargar los datos. Por favor, intente nuevamente.");
    } finally {
      setLoading(false);
    }
  };

  const guardarEdicionMarcacion = async (dni, day, tipo, valor) => {
    // Mapeo de tipos de la UI a los tipos de Firestore para asegurar consistencia
    const tipoFirestore = {
      ingreso: "Ingreso",
      salida: "Salida",
      refrigerioInicio: "Refrigerio_Inicio",
      refrigerioFin: "Refrigerio_Fin",
    }[tipo];

    // Si el tipo no está en el mapeo, es un error y no se debe continuar.
    if (!tipoFirestore) {
      console.error("Tipo de marcación no reconocido:", tipo);
      Swal.fire("Error", `Tipo de marcación no reconocido: ${tipo}`, "error");
      return;
    }

    try {
      setLoading(true);
      const registrosRef = collection(db, `asistencia/${dni}/registros`);

      // La consulta ahora usa el tipo correcto de Firestore (ej. "Ingreso")
      const q = query(
        registrosRef,
        where("fechaJornada", "==", day),
        where("tipo", "==", tipoFirestore)
      );
      const snap = await getDocs(q);

      if (!snap.empty) {
        // Encontrar el registro principal a actualizar (preferir esRegularizacion y más reciente)
        let docToUpdate = snap.docs[0];
        snap.docs.forEach((d) => {
          const data = d.data();
          const esReg = data.esRegularizacion === true || data.origen === "regularizacion-aprobada";
          const actualData = docToUpdate.data();
          const actualEsReg = actualData.esRegularizacion === true || actualData.origen === "regularizacion-aprobada";

          if (esReg && !actualEsReg) {
            docToUpdate = d;
          } else if (esReg && actualEsReg) {
            const nuevoTimestamp = data.timestamp && data.timestamp.toDate ? data.timestamp.toDate() : new Date(0);
            const actualTimestamp = actualData.timestamp && actualData.timestamp.toDate ? actualData.timestamp.toDate() : new Date(0);
            if (nuevoTimestamp > actualTimestamp) {
              docToUpdate = d;
            }
          }
        });

        const docId = docToUpdate.id;
        const dataToUpdate = docToUpdate.data();
        const historialAnterior = dataToUpdate.historialModificaciones || [];
        const historialNuevo = {
          fecha: new Date().toISOString(),
          modificadoPor: usuario?.email || "Administrador",
          valorAnterior: dataToUpdate.fechaMarcacion || null,
          valorNuevo: valor,
          tipoEdicion: "edicionManualTabla",
        };

        const docRef = doc(db, `asistencia/${dni}/registros`, docId);
        await updateDoc(docRef, {
          fechaMarcacion: valor,
          esModificadoManual: true,
          fechaUltimaModificacion: serverTimestamp(),
          modificadoPor: usuario?.email || usuario?.uid || "Administrador",
          historialModificaciones: [...historialAnterior, historialNuevo],
          esRegistroRetroactivo: true,
          origen: "edicion-admin",
          regularizado: true,
        });

        // AUDITORÍA
        try {
          await logBusinessAction("EDICION_MANUAL_ASISTENCIA", "GestionAsistencias", {
            dniEditado: dni,
            fechaJornada: day,
            registroId: docId,
            cambiosRealizados: {
              [tipoFirestore]: {
                anterior: dataToUpdate.fechaMarcacion || null,
                nuevo: valor
              }
            }
          }, usuario);
        } catch (e) {
          console.warn("No se pudo registrar la auditoría", e);
        }

        // Actualizar el estado local para reflejar el cambio inmediatamente
        setAttendanceData((prevData) =>
          prevData.map((record) =>
            record.id === docId ? {
              ...record,
              fechaMarcacion: valor,
              esModificadoManual: true,
              fechaUltimaModificacion: new Date(),
              modificadoPor: usuario?.email || usuario?.uid || "Administrador",
              historialModificaciones: [...historialAnterior, historialNuevo],
              esRegistroRetroactivo: true,
              origen: "edicion-admin",
              regularizado: true,
            } : record
          )
        );
      } else if (valor) {
        // Si no se encuentra y hay un valor, se crea un nuevo registro
        const historialNuevo = {
          fecha: new Date().toISOString(),
          modificadoPor: usuario?.email || "Administrador",
          valorAnterior: null,
          valorNuevo: valor,
          tipoEdicion: "creacionManualTabla",
        };

        const newRecordData = {
          dni,
          fechaJornada: day,
          tipo: tipoFirestore, // Usa el tipo correcto de Firestore
          fechaMarcacion: valor,
          ruc: rucEmpresa,
          ano: day.split("-")[0],
          periodo: day.split("-")[1],
          timestamp: serverTimestamp(),
          esModificadoManual: true,
          fechaUltimaModificacion: serverTimestamp(),
          creadoPor: usuario?.email || usuario?.uid || "Administrador",
          modificadoPor: usuario?.email || usuario?.uid || "Administrador",
          historialModificaciones: [historialNuevo],
          esRegistroRetroactivo: true,
          origen: "edicion-admin",
          regularizado: true,
        };
        const docRef = await addDoc(registrosRef, newRecordData);

        // AUDITORÍA
        try {
          await logBusinessAction("EDICION_MANUAL_ASISTENCIA", "GestionAsistencias", {
            dniEditado: dni,
            fechaJornada: day,
            registroId: docRef.id,
            cambiosRealizados: {
              [tipoFirestore]: {
                anterior: null,
                nuevo: valor
              }
            }
          }, usuario);
        } catch (e) {
          console.warn("No se pudo registrar la auditoría", e);
        }

        // Actualizar el estado local con el nuevo registro
        setAttendanceData((prevData) => [
          ...prevData,
          { ...newRecordData, id: docRef.id, timestamp: new Date() },
        ]);
      }

      // Limpia la celda editada
      setEditando((prev) => {
        const nuevo = { ...prev };
        delete nuevo[`${dni}-${day}-${tipo}`];
        return nuevo;
      });
    } catch (e) {
      console.error("Error al guardar la marcación:", e);
      Swal.fire("Error", "No se pudo guardar el cambio", "error");
    } finally {
      setLoading(false);
    }
  };

  // Función auxiliar para dividir un array en lotes
  const chunkArray = (array, size) => {
    const result = [];
    for (let i = 0; i < array.length; i += size) {
      result.push(array.slice(i, i + size));
    }
    return result;
  };

  // Función para cargar datos de un empleado específico
  const loadEmployeeData = async (
    dni,
    attendanceRecords,
    absenceRecords,
    year,
    month,
    startDate,
    endDate
  ) => {
    try {
      // Cargar asistencias
      const attendanceRef = collection(db, `asistencia/${dni}/registros`);
      const attendanceQuery = query(
        attendanceRef,
        where("ruc", "==", rucEmpresa),
        where("ano", "==", year),
        where("periodo", "==", month.padStart(2, "0")),
        limit(500) // Limitamos a 500 registros por empleado
      );

      const attendanceSnapshot = await getDocs(attendanceQuery);

      attendanceSnapshot.forEach((doc) => {
        attendanceRecords.push({
          id: doc.id,
          ...doc.data(),
        });
      });

      // Cargar ausencias
      const absenceRef = collection(db, `ausencias/${dni}/ausencias`);
      const absenceQuery = query(
        absenceRef,
        where("ruc", "==", rucEmpresa),
        limit(100) // Limitamos a 100 registros por empleado
      );

      const absenceSnapshot = await getDocs(absenceQuery);

      absenceSnapshot.forEach((doc) => {
        const absenceData = doc.data();
        // Solo procesamos si fechaInicio y fechaFin son válidos
        if (absenceData.fechaInicio && absenceData.fechaFin) {
          const absenceStart = absenceData.fechaInicio.toDate();
          const absenceEnd = absenceData.fechaFin.toDate();

          // Verificar si la ausencia coincide con el mes seleccionado
          if (
            isWithinInterval(startDate, {
              start: absenceStart,
              end: absenceEnd,
            }) ||
            isWithinInterval(endDate, {
              start: absenceStart,
              end: absenceEnd,
            }) ||
            isWithinInterval(absenceStart, { start: startDate, end: endDate })
          ) {
            absenceRecords.push({
              id: doc.id,
              ...absenceData,
            });
          }
        }
      });

      return {
        attendanceCount: attendanceSnapshot.size,
        absenceCount: absenceSnapshot.size,
      };
    } catch (error) {
      console.error(`Error al cargar datos del empleado ${dni}:`, error);
      return { attendanceCount: 0, absenceCount: 0 };
    }
  };

  // Función para obtener la lista de empleados - Optimizada
  const getEmployeeList = async () => {
    try {
      const usersRef = collection(db, "users");
      const usersQuery = query(
        usersRef,
        where("rucEmpresa", "==", rucEmpresa),
        where("activo", "==", true) // Solo activos — reduce documentos descargados
      );

      const usersSnapshot = await getDocs(usersQuery);
      const users = [];

      usersSnapshot.forEach((doc) => {
        users.push({ id: doc.id, ...doc.data() });
      });

      return filtrarPorDnisPermitidos(
        users,
        (user) => user?.dni,
        dnisPermitidos
      );
    } catch (err) {
      console.error("Error al obtener lista de empleados:", err);
      return [];
    }
  };

  // Función para calcular estadísticas
  const calculateStatistics = () => {
    if (filteredAttendance.length === 0 && filteredAbsences.length === 0) {
      setStatistics({
        totalEmployees: 0,
        attendanceRate: 0,
        absenceRate: 0,
        lateArrivals: 0,
        absencesByType: {},
        attendanceByDay: {},
        objetivoAFecha: 0,
        laborables: 0,
      });
      return;
    }

    // APLICAR FILTRO DE REGULARIZACIONES
    const registrosFiltrados = filtrarPreferirRegularizacion(filteredAttendance);

    // Calcular empleados únicos
    const uniqueEmployees = new Set(
      registrosFiltrados.map((record) => record.dni)
    );
    filteredAbsences.forEach((record) => uniqueEmployees.add(record.dni));

    // Calcular días laborables (solo lunes a viernes)
    const [year, month] = selectedMonth.split("-");
    const daysInMonth = new Date(year, month, 0).getDate();
    let laborables = 0;
    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(year, month - 1, d);
      const dayOfWeek = date.getDay();
      if (dayOfWeek >= 1 && dayOfWeek <= 5) laborables++;
    }
    const workingDays = laborables * uniqueEmployees.size;

    // Calcular marcaciones completas (Ingreso y Salida)
    const completosPorEmpleado = {};
    registrosFiltrados.forEach((record) => {
      if (!record.dni || !record.fechaJornada) return;
      const key =
        record.dni +
        "-" +
        (typeof record.fechaJornada === "string"
          ? record.fechaJornada
          : format(record.fechaJornada.toDate(), "yyyy-MM-dd"));
      if (!completosPorEmpleado[key])
        completosPorEmpleado[key] = { ingreso: false, salida: false };
      if (record.tipo === "Ingreso") completosPorEmpleado[key].ingreso = true;
      if (record.tipo === "Salida") completosPorEmpleado[key].salida = true;
    });
    const diasCompletos = Object.values(completosPorEmpleado).filter(
      (v) => v.ingreso && v.salida
    ).length;

    // Tasa de asistencia
    const attendanceRate =
      workingDays > 0 ? (diasCompletos / workingDays) * 100 : 0;

    // Calcular objetivo a la fecha
    const hoy = new Date();
    const esMesActual =
      Number(year) === hoy.getFullYear() &&
      Number(month) === hoy.getMonth() + 1;
    let laborablesHastaHoy = 0;
    if (esMesActual) {
      for (let d = 1; d <= hoy.getDate(); d++) {
        const date = new Date(year, month - 1, d);
        const dayOfWeek = date.getDay();
        if (dayOfWeek >= 1 && dayOfWeek <= 5) laborablesHastaHoy++;
      }
    } else {
      laborablesHastaHoy = laborables;
    }
    const objetivoAFecha =
      workingDays > 0
        ? ((laborablesHastaHoy * uniqueEmployees.size) / workingDays) * 100
        : 0;

    // Calcular tasa de ausencia
    let absenceDays = 0;
    filteredAbsences.forEach((absence) => {
      if (!absence.fechaInicio || !absence.fechaFin) return;

      const startDate = absence.fechaInicio.toDate();
      const endDate = absence.fechaFin.toDate();

      const rangeStart = new Date(
        Math.max(startDate.getTime(), new Date(year, month - 1, 1).getTime())
      );
      const rangeEnd = new Date(
        Math.min(endDate.getTime(), new Date(year, month, 0).getTime())
      );

      const daysInRange = differenceInDays(rangeEnd, rangeStart) + 1;
      absenceDays += daysInRange;
    });

    // Calcular llegadas tarde
    const lateArrivals = registrosFiltrados.filter((record) => {
      if (record.tipo !== "Ingreso") return false;

      const timeStr = record.fechaMarcacion;
      if (!timeStr) return false;

      try {
        const [hour, minute] = timeStr.split(":").map(Number);
        return hour > startHour || (hour === startHour && minute > startMinute);
      } catch (err) {
        return false;
      }
    }).length;

    // Calcular ausencias por tipo
    const absencesByType = {};
    filteredAbsences.forEach((absence) => {
      const type = absence.tipo || "No especificado";
      absencesByType[type] = (absencesByType[type] || 0) + 1;
    });

    // Calcular asistencias por día
    const attendanceByDay = {};
    registrosFiltrados.forEach((record) => {
      if (!record.timestamp) return;

      try {
        const date = format(record.timestamp.toDate(), "yyyy-MM-dd");
        if (record.tipo === "Ingreso") {
          attendanceByDay[date] = (attendanceByDay[date] || 0) + 1;
        }
      } catch (err) {
        console.error("Error al formatear fecha:", err);
      }
    });

    // Actualizar estadísticas
    setStatistics({
      totalEmployees: uniqueEmployees.size,
      attendanceRate,
      absenceRate: workingDays > 0 ? (absenceDays / workingDays) * 100 : 0,
      lateArrivals,
      absencesByType,
      attendanceByDay,
      objetivoAFecha,
      laborables,
    });
  };

  // Función auxiliar para obtener la lista de empleados filtrados por grupo
  const getEmpleadosFiltradosPorGrupo = () => {
    if (grupoSeleccionado === "todos") return employeeList;
    const grupo = grupos.find((g) => g.id === grupoSeleccionado);
    if (grupo) {
      return employeeList.filter((emp) => grupo.members.includes(emp.dni));
    }
    return [];
  };

  // Función para procesar los datos de asistencia para la tabla de marcaciones diarias
  const processAttendanceForTable = () => {
    if (filteredAttendance.length === 0) return [];

    const [year, month] = selectedMonth.split("-");
    const hoy = new Date();
    const esMesActual =
      Number(year) === hoy.getFullYear() &&
      Number(month) === hoy.getMonth() + 1;
    const lastDay = esMesActual
      ? hoy.getDate()
      : new Date(year, month, 0).getDate();

    // Usar empleados filtrados por grupo y, si hay, por el filtro de DNI:
    // las filas de las tablas deben respetar el DNI buscado, no listar a todos
    let empleadosParaTabla =
      selectedEmployee === "todos"
        ? getEmpleadosFiltradosPorGrupo()
        : employeeList.filter((e) => e.dni === selectedEmployee);

    if (dnisFiltroDni) {
      const grupoActual =
        grupoSeleccionado !== "todos"
          ? grupos.find((g) => g.id === grupoSeleccionado)
          : null;
      const enGrupo = (dni) =>
        !grupoActual || grupoActual.members.includes(dni);

      const baseFiltrada = empleadosParaTabla.filter((e) =>
        dnisFiltroDni.has(String(e.dni))
      );
      // Trabajadores del filtro DNI que no están en la lista del mes
      // (p. ej. inactivos): se añaden para que sus marcaciones tengan fila
      const yaIncluidos = new Set(baseFiltrada.map((e) => String(e.dni)));
      const extras = [...dnisFiltroDni]
        .filter((dni) => !yaIncluidos.has(dni) && enGrupo(dni))
        .map((dni) => {
          const encontrado = empleadosPorDni.find(
            (e) => String(e.dni) === dni
          );
          return {
            dni,
            nombre: encontrado?.nombre || `DNI ${dni}`,
            centroNombre: encontrado?.centroNombre || "",
          };
        });

      empleadosParaTabla = [...baseFiltrada, ...extras];
    }

    // Crear un mapa para organizar los datos: empleado -> fechaJornada -> tipo de marcación
    const attendanceMap = new Map();

    empleadosParaTabla.forEach((employee) => {
      const dayMap = {};
      for (let day = 1; day <= lastDay; day++) {
        const fecha = `${year}-${month.padStart(2, "0")}-${day
          .toString()
          .padStart(2, "0")}`;
        dayMap[fecha] = {
          ingreso: null,
          salida: null,
          centroNombre: null,
          refrigerioInicio: null,
          refrigerioFin: null,
        };
      }
      attendanceMap.set(employee.dni, {
        dni: employee.dni,
        nombre: employee.nombre,
        dias: dayMap,
        centroNombre: "",
      });
    });

    // APLICAR FILTRO DE REGULARIZACIONES
    const registrosFiltrados = filtrarPreferirRegularizacion(filteredAttendance);

    registrosFiltrados.forEach((record) => {
      if (!record.fechaJornada) return;

      const fechaJornada =
        typeof record.fechaJornada === "string"
          ? record.fechaJornada
          : format(record.fechaJornada.toDate(), "yyyy-MM-dd");

      if (!fechaJornada.startsWith(`${year}-${month.padStart(2, "0")}`)) {
        return;
      }

      const employeeDni = record.dni;
      if (!attendanceMap.has(employeeDni)) return;

      const employeeData = attendanceMap.get(employeeDni);
      const dayData = employeeData.dias[fechaJornada];
      if (!dayData) return;

      const centroNombreRegistro =
        record.centro?.centroNombre || record.centroNombre || "";
      if (centroNombreRegistro) {
        dayData.centroNombre = centroNombreRegistro;
        employeeData.centroNombre = centroNombreRegistro;
      }

      if (record.tipo === "Ingreso") {
        dayData.ingreso =
          record.fechaMarcacion || format(record.timestamp.toDate(), "HH:mm");
        dayData.ingresoModificado = record.esModificadoManual === true;
      } else if (record.tipo === "Salida") {
        dayData.salida =
          record.fechaMarcacion || format(record.timestamp.toDate(), "HH:mm");
        dayData.salidaModificado = record.esModificadoManual === true;
      } else if (
        record.tipo === "Refrigerio-Inicio" ||
        record.tipo === "Refrigerio_Inicio"
      ) {
        dayData.refrigerioInicio =
          record.fechaMarcacion || format(record.timestamp.toDate(), "HH:mm");
        dayData.refrigerioInicioModificado = record.esModificadoManual === true;
      } else if (
        record.tipo === "Refrigerio-Fin" ||
        record.tipo === "Refrigerio_Fin"
      ) {
        dayData.refrigerioFin =
          record.fechaMarcacion || format(record.timestamp.toDate(), "HH:mm");
        dayData.refrigerioFinModificado = record.esModificadoManual === true;
      }
    });

    // Convertir el mapa a array para la tabla
    const tableData = [];
    attendanceMap.forEach((employeeData) => {
      tableData.push({
        dni: employeeData.dni,
        nombre: employeeData.nombre,
        dias: employeeData.dias,
        centroNombre: employeeData.centroNombre || "",
      });
    });

    let filteredData = tableData;

    if (busquedaEmpleado) {
      const searchTerm = busquedaEmpleado.toLowerCase();
      filteredData = filteredData.filter(
        (employee) =>
          employee.nombre.toLowerCase().includes(searchTerm) ||
          employee.dni.includes(searchTerm)
      );
    }

    if (mostrarSoloConMarcacion) {
      filteredData = filteredData.filter((employee) => {
        return Object.values(employee.dias).some(
          (dayData) => dayData.ingreso || dayData.salida
        );
      });
    }

    // Ordenar alfabéticamente por nombre (comportamiento por defecto fijo)
    filteredData.sort((a, b) => a.nombre.localeCompare(b.nombre));

    return filteredData;
  };

  // Función para calcular horas trabajadas
  const calcularHorasTrabajadas = (horaIngreso, horaSalida) => {
    if (!horaIngreso || !horaSalida) return null;

    try {
      // Convertir HH:mm a minutos para facilitar el cálculo
      const [horasIngreso, minutosIngreso] = horaIngreso.split(":").map(Number);
      const [horasSalida, minutosSalida] = horaSalida.split(":").map(Number);

      const totalMinutosIngreso = horasIngreso * 60 + minutosIngreso;
      const totalMinutosSalida = horasSalida * 60 + minutosSalida;

      // Calcular diferencia en minutos
      let diferenciaMinutos = totalMinutosSalida - totalMinutosIngreso;

      // Si la diferencia es negativa, asumimos que salió al día siguiente (24 horas después)
      if (diferenciaMinutos < 0) {
        diferenciaMinutos += 24 * 60;
      }

      // Convertir minutos a formato HH:mm
      const horas = Math.floor(diferenciaMinutos / 60);
      const minutos = diferenciaMinutos % 60;

      return `${horas.toString().padStart(2, "0")}:${minutos
        .toString()
        .padStart(2, "0")}`;
    } catch (error) {
      console.error("Error al calcular horas trabajadas:", error);
      return null;
    }
  };

  // Configuración para los gráficos
  const pieChartData = {
    labels: ["Asistencias", "Ausencias", "Llegadas tarde"],
    datasets: [
      {
        data: [
          statistics.attendanceRate.toFixed(1),
          statistics.absenceRate.toFixed(1),
          (
            (statistics.lateArrivals / (filteredAttendance.length || 1)) *
            100
          ).toFixed(1),
        ],
        backgroundColor: ["#4CAF50", "#F44336", "#FFC107"],
        borderWidth: 1,
      },
    ],
  };

  const barChartOptions = {
    responsive: true,
    plugins: {
      legend: { position: "top" },
      title: { display: true, text: "Ausencias por tipo" },
    },
  };

  const barChartData = {
    labels: Object.keys(statistics.absencesByType),
    datasets: [
      {
        label: "Número de ausencias",
        data: Object.values(statistics.absencesByType),
        backgroundColor: "#3f51b5",
        borderColor: "#303f9f",
        borderWidth: 1,
      },
    ],
  };

  // Datos para gráfico de línea de asistencia diaria
  const getDailyAttendanceData = () => {
    const [year, month] = selectedMonth.split("-");
    const hoy = new Date();
    const esMesActual =
      Number(year) === hoy.getFullYear() &&
      Number(month) === hoy.getMonth() + 1;
    const lastDay = esMesActual
      ? hoy.getDate()
      : new Date(year, month, 0).getDate();

    const labels = Array.from({ length: lastDay }, (_, i) => `${i + 1}`);
    const attendanceData = labels.map((day) => {
      const date = `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
      return statistics.attendanceByDay[date] || 0;
    });

    return {
      labels,
      datasets: [
        {
          label: "Asistencias diarias",
          data: attendanceData,
          fill: false,
          borderColor: "#2196f3",
          tension: 0.1,
        },
      ],
    };
  };

  // Función para exportar reporte a Excel
  // Agrega esta función para obtener los horarios y coordenadas por planta:
  const getHorariosPorPlanta = async (dni, nombrePlanta) => {
    try {
      const coordRef = collection(db, `asistencia/${dni}/coordenadas`);
      const coordQuery = query(
        coordRef,
        where("nombre", "==", nombrePlanta),
        where("activo", "==", true),
        limit(1)
      );
      const coordSnap = await getDocs(coordQuery);
      if (!coordSnap.empty) {
        const data = coordSnap.docs[0].data();
        return {
          horarioIngreso: data.horarioIngreso || "",
          horarioSalida: data.horarioSalida || "",
          nombrePlanta: data.nombre || "",
          latitud: data.latitud,
          longitud: data.longitud,
        };
      }
      return null;
    } catch (e) {
      console.error("Error obteniendo horarios por planta:", e);
      return null;
    }
  };

  // Devuelve solo un registro por dni-fecha-tipo, prefiriendo regularización si existe
  const filtrarPreferirRegularizacion = (registros) => {
    const map = new Map();

    registros.forEach((r) => {
      const key = `${r.dni}-${typeof r.fechaJornada === "string" ? r.fechaJornada : format(r.fechaJornada.toDate(), "yyyy-MM-dd")}-${r.tipo}`;
      const esReg = r.esRegularizacion === true || r.origen === "regularizacion-aprobada";

      if (!map.has(key)) {
        map.set(key, r);
      } else {
        const actual = map.get(key);
        const actualEsReg = actual.esRegularizacion === true || actual.origen === "regularizacion-aprobada";

        // Si el nuevo es regularización y el actual no, reemplaza
        if (esReg && !actualEsReg) {
          map.set(key, r);
        }
        // Si ambos son regularizaciones, elige el más reciente por timestamp
        else if (esReg && actualEsReg) {
          const nuevoTimestamp = r.timestamp ? r.timestamp.toDate() : new Date(0);
          const actualTimestamp = actual.timestamp ? actual.timestamp.toDate() : new Date(0);
          if (nuevoTimestamp > actualTimestamp) {
            map.set(key, r);
          }
        }
        // Si ninguno es regularización, mantiene el primero
      }
    });

    return Array.from(map.values());
  };

  const obtenerFechaRegistro = (record) => {
    if (record?.timestamp?.toDate) return record.timestamp.toDate();
    if (record?.fechaReal) {
      const d = new Date(record.fechaReal);
      if (!Number.isNaN(d.getTime())) return d;
    }
    if (record?.timestampLocal) {
      const d = new Date(record.timestampLocal);
      if (!Number.isNaN(d.getTime())) return d;
    }
    if (record?.createdAt) {
      const d = new Date(record.createdAt);
      if (!Number.isNaN(d.getTime())) return d;
    }
    if (record?.fechaJornada && record?.fechaMarcacion) {
      const d = new Date(`${record.fechaJornada}T${record.fechaMarcacion}`);
      if (!Number.isNaN(d.getTime())) return d;
    }
    return null;
  };

  const formatearFechaRegistro = (record) => {
    if (record?.fechaHoraPeru) return String(record.fechaHoraPeru);
    const d = obtenerFechaRegistro(record);
    return d ? format(d, "yyyy-MM-dd HH:mm:ss") : "—";
  };

  const getRegistrosFiltradosOrdenados = () => {
    let registros = [...filteredAttendance];

    if (filtroTipoRegistro !== "todos") {
      registros = registros.filter(
        (r) => String(r?.tipo || "").toLowerCase() === filtroTipoRegistro.toLowerCase()
      );
    }

    if (busquedaRegistro.trim()) {
      const token = busquedaRegistro.trim().toLowerCase();
      registros = registros.filter((r) => {
        const campos = [
          r?.dni,
          r?.nombre,
          r?.apellidos,
          r?.email,
          r?.cargo,
          r?.tipo,
          r?.origen,
          r?.centro?.centroNombre,
          r?.centroNombre,
          r?.fechaJornada,
          r?.fechaMarcacion,
        ];
        return campos.some((c) => String(c || "").toLowerCase().includes(token));
      });
    }

    return registros
      .map((r) => ({ ...r, _fechaOrden: obtenerFechaRegistro(r) }))
      .sort((a, b) => {
        const ta = a._fechaOrden ? a._fechaOrden.getTime() : 0;
        const tb = b._fechaOrden ? b._fechaOrden.getTime() : 0;
        return tb - ta;
      });
  };

  const getRegistrosPaginados = () => {
    const enriquecidos = getRegistrosFiltradosOrdenados();

    const totalItems = enriquecidos.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / elementosPorPaginaRegistros));
    const currentPage = Math.min(Math.max(1, paginaRegistros), totalPages);

    if (currentPage !== paginaRegistros) {
      setPaginaRegistros(currentPage);
    }

    const startIndex = (currentPage - 1) * elementosPorPaginaRegistros;
    const endIndex = Math.min(startIndex + elementosPorPaginaRegistros, totalItems);
    const items = enriquecidos.slice(startIndex, endIndex);

    return {
      items,
      currentPage,
      totalPages,
      totalItems,
      startIndex,
      endIndex,
    };
  };

  // Modifica la función exportReport para incluir los horarios en el Excel Detalle de Horas:
  const exportReport = async (format) => {
    if (format === "excel") {
      try {
        setExportando(true);

        const tableData = processAttendanceForTable();
        const activeTabText =
          document.querySelector(".nav-link.active")?.textContent || "";
        const isRegistrosTab = activeTabText.includes("Registros");

        if (!isRegistrosTab && (!tableData || tableData.length === 0)) {
          Swal.fire({
            icon: "warning",
            title: "Sin datos",
            text: "No hay datos para exportar en este período.",
          });
          setExportando(false);
          return;
        }
        const wb = XLSX.utils.book_new();

        // Los cargos ya están en employeeList gracias a la optimización en initializeComponent
        const cargosPorEmpleado = {};
        employeeList.forEach(emp => {
          cargosPorEmpleado[emp.dni] = emp.cargo || "—";
        });

        // --- Detalle de Horas ---
        if (
          document
            .querySelector(".nav-link.active")
            .textContent.includes("Detalle de Horas")
        ) {
          // Optimización: Pre-procesar regularizaciones UNA vez
          const registrosFiltradosDetalle = filtrarPreferirRegularizacion(filteredAttendance);

          // Optimización: Mapa de búsqueda rápida DNI-Fecha para O(1)
          const recordsLookup = new Map();
          registrosFiltradosDetalle.forEach(r => {
            const fStr = typeof r.fechaJornada === "string"
              ? r.fechaJornada
              : format(r.fechaJornada.toDate(), "yyyy-MM-dd");
            recordsLookup.set(`${r.dni}-${fStr}`, r);
          });

          // Optimización: Caché de horarios para evitar redundancia
          const schedulesCache = {};
          const getCachedSchedules = async (dni, nombrePlanta) => {
            const cacheKey = `${dni}-${nombrePlanta}`;
            if (schedulesCache[cacheKey]) return schedulesCache[cacheKey];
            const result = await getHorariosPorPlanta(dni, nombrePlanta);
            schedulesCache[cacheKey] = result || {};
            return schedulesCache[cacheKey];
          };

          // Pre-cargar horarios en paralelo
          const distinctRequests = [];
          tableData.forEach(emp => {
            const primerDia = Object.values(emp.dias).find(d => d.centroNombre);
            const p = primerDia?.centroNombre || emp.centroNombre || "";
            if (p) distinctRequests.push({ dni: emp.dni, planta: p });
          });
          await Promise.all(distinctRequests.map(item => getCachedSchedules(item.dni, item.planta)));

          const headers = [
            "N°",
            "DNI",
            "Empleado",
            "Cargo",
            "Centro",
            "Día",
            "H Ingreso Horario",
            "H. Ingreso Marcado",
            "Tardanza (min)",
            "H. Salida Horario",
            "H. Salida Marcado",
            "Salida (min)",
            "Inicio Refrigerio",
            "Fin Refrigerio",
            "Horas Refrigerio",
            "Horas Trabajadas",
            "Estado",
          ];

          let rowIndex = 1;
          const wsData = tableData.flatMap((employee) =>
            Object.keys(employee.dias).map((day) => {
              const dayData = employee.dias[day];
              const registro = recordsLookup.get(`${employee.dni}-${day}`);

              const primerDia = Object.values(employee.dias).find(d => d.centroNombre);
              const np = primerDia?.centroNombre || employee.centroNombre || "";
              const horarios = schedulesCache[`${employee.dni}-${np}`] || {};
              const hIngresoHorario = horarios.horarioIngreso || "—";
              const hSalidaHorario = horarios.horarioSalida || "—";

              const hIngresoMarcado = dayData.ingreso || "—";
              const hSalidaMarcado = dayData.salida || "—";

              let tardanzaMin = "—";
              if (hIngresoHorario !== "—" && hIngresoMarcado !== "—") {
                const [h1, m1] = hIngresoMarcado.split(":").map(Number);
                const [h2, m2] = hIngresoHorario.split(":").map(Number);
                tardanzaMin = h1 * 60 + m1 - (h2 * 60 + m2);
                if (tardanzaMin < 0) tardanzaMin = 0;
              }

              let salidaMin = "—";
              if (hSalidaHorario !== "—" && hSalidaMarcado !== "—") {
                const [h1, m1] = hSalidaMarcado.split(":").map(Number);
                const [h2, m2] = hSalidaHorario.split(":").map(Number);
                salidaMin = h1 * 60 + m1 - (h2 * 60 + m2);
              }

              let resultadoIngreso = "—";
              if (hIngresoHorario !== "—" && hIngresoMarcado !== "—") {
                const [h1, m1] = hIngresoMarcado.split(":").map(Number);
                const [h2, m2] = hIngresoHorario.split(":").map(Number);
                if (h1 > h2 || (h1 === h2 && m1 > m2)) {
                  resultadoIngreso = "Tardanza";
                } else {
                  resultadoIngreso = "A tiempo";
                }
              }

              let resultadoSalida = "—";
              if (hSalidaHorario !== "—" && hSalidaMarcado !== "—") {
                const [h1, m1] = hSalidaMarcado.split(":").map(Number);
                const [h2, m2] = hSalidaHorario.split(":").map(Number);
                if (h1 < h2 || (h1 === h2 && m1 < m2)) {
                  resultadoSalida = "Salida temprana";
                } else {
                  resultadoSalida = "Salida posterior";
                }
              }

              let fechaExcel = day;
              if (registro && registro.fechaJornada) {
                let d;
                if (typeof registro.fechaJornada === "string") {
                  const [anio, mes, dia] = registro.fechaJornada.split("-");
                  fechaExcel = new Date(
                    Number(anio),
                    Number(mes) - 1,
                    Number(dia),
                    12,
                    0,
                    0
                  );
                } else if (registro.fechaJornada.toDate) {
                  d = registro.fechaJornada.toDate();
                  fechaExcel = new Date(
                    d.getFullYear(),
                    d.getMonth(),
                    d.getDate(),
                    12,
                    0,
                    0
                  );
                }
              } else {
                const [anio, mes, dia] = day.split("-");
                fechaExcel = new Date(
                  Number(anio),
                  Number(mes) - 1,
                  Number(dia),
                  12,
                  0,
                  0
                );
              }

              let horasRefrigerio = null;
              if (dayData.refrigerioInicio && dayData.refrigerioFin) {
                horasRefrigerio = calcularHorasTrabajadas(
                  dayData.refrigerioInicio,
                  dayData.refrigerioFin
                );
              }

              let horasTrabajadas = calcularHorasTrabajadas(
                dayData.ingreso,
                dayData.salida
              );
              if (horasTrabajadas && horasRefrigerio) {
                const [hT, mT] = horasTrabajadas.split(":").map(Number);
                const [hR, mR] = horasRefrigerio.split(":").map(Number);
                let totalMin = hT * 60 + mT - (hR * 60 + mR);
                if (totalMin < 0) totalMin = 0;
                const hFinal = Math.floor(totalMin / 60);
                const mFinal = totalMin % 60;
                horasTrabajadas = `${hFinal
                  .toString()
                  .padStart(2, "0")}:${mFinal.toString().padStart(2, "0")}`;
              }

              let estado = "No marcó";
              if (dayData.ingreso && dayData.salida) {
                estado = "Completo";
              } else if (dayData.ingreso) {
                estado = "Solo ingreso";
              } else if (dayData.salida) {
                estado = "Solo salida";
              }

              return [
                rowIndex++,
                employee.dni,
                employee.nombre,
                cargosPorEmpleado[employee.dni] || "—",
                dayData.centroNombre || employee.centroNombre || "—",
                fechaExcel,
                hIngresoHorario,
                hIngresoMarcado,
                tardanzaMin !== "—" ? tardanzaMin : "—",
                hSalidaHorario,
                hSalidaMarcado,
                salidaMin !== "—" ? salidaMin : "—",
                dayData.refrigerioInicio || "—",
                dayData.refrigerioFin || "—",
                horasRefrigerio || "—",
                horasTrabajadas || "—",
                estado,
              ];
            })
          );

          wsData.unshift(headers);

          const ws = XLSX.utils.aoa_to_sheet(wsData);
          const diaIndex = headers.indexOf("Día");
          if (diaIndex !== -1) {
            const colLetter = XLSX.utils.encode_col(diaIndex);
            for (let i = 2; i <= wsData.length; i++) {
              const cell = ws[`${colLetter}${i}`];
              if (cell && cell.v instanceof Date) {
                cell.t = "d";
                cell.z = "dd/mm/yyyy";
              }
            }
          }
          XLSX.utils.book_append_sheet(wb, ws, "Detalle Horas");
        }

        // --- Detalles ---
        else if (
          document
            .querySelector(".nav-link.active")
            .textContent.includes("Detalles")
        ) {
          const [year, month] = selectedMonth.split("-");
          const hoy = new Date();
          const esMesActual =
            Number(year) === hoy.getFullYear() &&
            Number(month) === hoy.getMonth() + 1;
          const lastDay = esMesActual
            ? hoy.getDate()
            : new Date(year, month, 0).getDate();

          const headers = [
            "N°",
            "DNI",
            "Empleado",
            "Cargo",
            "Planta",
            ...Array.from({ length: lastDay }, (_, i) =>
              (i + 1).toString().padStart(2, "0")
            ),
            "DIAS TRABAJADOS",
            "INASISTENCIAS",
          ];

          const wsData = [headers];
          tableData.forEach((employee, idx) => {
            let diasTrabajados = 0;
            let inasistencias = 0;
            let planta = "";
            for (const day of Object.keys(employee.dias)) {
              const centro = employee.dias[day]?.centroNombre;
              if (centro) {
                planta = centro;
                break;
              }
            }
            const row = [
              idx + 1,
              employee.dni,
              employee.nombre,
              cargosPorEmpleado[employee.dni] || "—",
              planta || employee.centroNombre || "—",
            ];
            for (let d = 1; d <= lastDay; d++) {
              const fecha = `${year}-${month.padStart(2, "0")}-${d
                .toString()
                .padStart(2, "0")}`;
              const dayData = employee.dias[fecha];
              const totalMarcaciones = dayData
                ? [
                    dayData.ingreso,
                    dayData.refrigerioInicio,
                    dayData.refrigerioFin,
                    dayData.salida,
                  ].filter(Boolean).length
                : 0;

              if (totalMarcaciones === 4) {
                row.push("VERDADERO");
                diasTrabajados++;
              } else {
                row.push("FALSO");
                inasistencias++;
              }
            }
            row.push(diasTrabajados);
            row.push(inasistencias);
            wsData.push(row);
          });

          const ws = XLSX.utils.aoa_to_sheet(wsData);
          XLSX.utils.book_append_sheet(wb, ws, "Detalles");
        }

        // --- Registros ---
        else if (
          document
            .querySelector(".nav-link.active")
            .textContent.includes("Registros")
        ) {
          const registrosExport = getRegistrosFiltradosOrdenados();

          if (registrosExport.length === 0) {
            Swal.fire({
              icon: "warning",
              title: "Sin datos",
              text: "No hay registros para exportar con los filtros actuales.",
            });
            setExportando(false);
            return;
          }

          const columnasVisiblesExport = columnasRegistrosConfig.filter((c) => c.visible);
          const headers = ["N°", ...columnasVisiblesExport.map((c) => c.label)];
          const wsData = [headers];

          registrosExport.forEach((r, idx) => {
            const row = [
              idx + 1,
              ...columnasVisiblesExport.map((c) => c.getValue(r, formatearFechaRegistro)),
            ];
            wsData.push(row);
          });

          const ws = XLSX.utils.aoa_to_sheet(wsData);
          XLSX.utils.book_append_sheet(wb, ws, "Registros");
        }

        // --- Resumen por Empleado ---
        else if (
          document
            .querySelector(".nav-link.active")
            .textContent.includes("Resumen por Empleado")
        ) {
          const headers = [
            "N°",
            "DNI",
            "Empleado",
            "Cargo",
            "Días Trabajados",
            "Días Completos",
            "Solo Entradas",
            "Solo Salidas",
            "Horas Totales",
            "Promedio Diario",
          ];
          const wsData = [headers];
          tableData.forEach((employee, idx) => {
            const diasCompletos = Object.values(employee.dias).filter(
              (d) => d.ingreso && d.salida
            ).length;
            const soloEntradas = Object.values(employee.dias).filter(
              (d) => d.ingreso && !d.salida
            ).length;
            const soloSalidas = Object.values(employee.dias).filter(
              (d) => !d.ingreso && d.salida
            ).length;

            let horasTotales = 0;
            let minutosTotales = 0;
            Object.values(employee.dias).forEach((d) => {
              if (d.ingreso && d.salida) {
                const [h1, m1] = d.ingreso.split(":").map(Number);
                const [h2, m2] = d.salida.split(":").map(Number);
                let min = h2 * 60 + m2 - (h1 * 60 + m1);
                if (min < 0) min += 24 * 60;
                horasTotales += Math.floor(min / 60);
                minutosTotales += min % 60;
              }
            });
            horasTotales += Math.floor(minutosTotales / 60);
            minutosTotales = minutosTotales % 60;
            const horasTotalesStr = `${horasTotales}:${minutosTotales
              .toString()
              .padStart(2, "0")}`;
            const promedioDiario =
              diasCompletos > 0
                ? `${(horasTotales / diasCompletos).toFixed(1)}h`
                : "—";
            wsData.push([
              idx + 1,
              employee.dni,
              employee.nombre,
              cargosPorEmpleado[employee.dni] || "—",
              diasCompletos + soloEntradas + soloSalidas,
              diasCompletos,
              soloEntradas,
              soloSalidas,
              horasTotalesStr,
              promedioDiario,
            ]);
          });
          const ws = XLSX.utils.aoa_to_sheet(wsData);
          XLSX.utils.book_append_sheet(wb, ws, "Resumen por Empleado");
        }

        XLSX.writeFile(wb, `Reporte_Asistencia_${selectedMonth}.xlsx`);
        Swal.fire({
          icon: "success",
          title: "Reporte exportado correctamente",
          text: "Se ha generado el archivo de Excel con los datos del reporte.",
        });
      } catch (error) {
        console.error("Error al exportar a Excel:", error);
        Swal.fire({
          icon: "error",
          title: "Error al exportar",
          text: "No se pudo generar el archivo de Excel. Intente nuevamente.",
        });
      } finally {
        setExportando(false);
      }
    }
  };

  // Filtrar y paginar los datos para la vista de Detalle de Horas
  const getDetalleHorasPaginado = () => {
    const tableData = processAttendanceForTable();

    // Solo filtrar por días completos, solo ingreso, solo salida, sin marcación
    const filteredData = tableData.flatMap((employee) =>
      Object.keys(employee.dias)
        .filter((day) => {
          const dayData = employee.dias[day];

          // Aplicar filtro por estado SOLO de asistencia
          if (filtroEstado === "completo") {
            return dayData.ingreso && dayData.salida;
          } else if (filtroEstado === "soloIngreso") {
            return dayData.ingreso && !dayData.salida;
          } else if (filtroEstado === "soloSalida") {
            return !dayData.ingreso && dayData.salida;
          } else if (filtroEstado === "sinMarca") {
            return !dayData.ingreso && !dayData.salida;
          }
          // No filtrar por tardanza ni ausencias
          return true;
        })
        .map((day) => ({
          employeeDni: employee.dni,
          employeeNombre: employee.nombre,
          day,
          dayData: employee.dias[day],
        }))
    );

    // Calcular índices para paginación
    const totalItems = filteredData.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / elementosPorPagina));

    // Asegurar que la página actual es válida
    const currentPage = Math.min(Math.max(1, paginaActual), totalPages);
    if (currentPage !== paginaActual) {
      setPaginaActual(currentPage);
    }

    const startIndex = (currentPage - 1) * elementosPorPagina;
    const endIndex = Math.min(startIndex + elementosPorPagina, totalItems);

    // Obtener los elementos de la página actual
    const itemsCurrentPage = filteredData.slice(startIndex, endIndex);

    return {
      items: itemsCurrentPage,
      currentPage,
      totalPages,
      totalItems,
      startIndex,
      endIndex,
    };
  };

  // Renderizar la paginación
  const renderPagination = (paginationData) => {
    if (!paginationData || paginationData.totalPages <= 1) return null;

    const { currentPage, totalPages } = paginationData;

    // Calcular rango de páginas a mostrar (máximo 5)
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = startPage + maxPagesToShow - 1;

    if (endPage > totalPages) {
      endPage = totalPages;
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    const pages = [];
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <Pagination size="sm" className="mb-0">
        <Pagination.First
          onClick={() => setPaginaActual(1)}
          disabled={currentPage === 1}
        />
        <Pagination.Prev
          onClick={() => setPaginaActual(currentPage - 1)}
          disabled={currentPage === 1}
        />

        {startPage > 1 && (
          <>
            <Pagination.Item onClick={() => setPaginaActual(1)}>
              1
            </Pagination.Item>
            {startPage > 2 && <Pagination.Ellipsis disabled />}
          </>
        )}

        {pages.map((page) => (
          <Pagination.Item
            key={page}
            active={page === currentPage}
            onClick={() => setPaginaActual(page)}
          >
            {page}
          </Pagination.Item>
        ))}

        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && <Pagination.Ellipsis disabled />}
            <Pagination.Item onClick={() => setPaginaActual(totalPages)}>
              {totalPages}
            </Pagination.Item>
          </>
        )}

        <Pagination.Next
          onClick={() => setPaginaActual(currentPage + 1)}
          disabled={currentPage === totalPages}
        />
        <Pagination.Last
          onClick={() => setPaginaActual(totalPages)}
          disabled={currentPage === totalPages}
        />
      </Pagination>
    );
  };

  const renderPaginationRegistros = (paginationData) => {
    if (!paginationData || paginationData.totalPages <= 1) return null;

    const { currentPage, totalPages } = paginationData;
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = startPage + maxPagesToShow - 1;

    if (endPage > totalPages) {
      endPage = totalPages;
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    const pages = [];
    for (let i = startPage; i <= endPage; i++) pages.push(i);

    return (
      <Pagination size="sm" className="mb-0">
        <Pagination.First
          onClick={() => setPaginaRegistros(1)}
          disabled={currentPage === 1}
        />
        <Pagination.Prev
          onClick={() => setPaginaRegistros(currentPage - 1)}
          disabled={currentPage === 1}
        />

        {pages.map((page) => (
          <Pagination.Item
            key={page}
            active={page === currentPage}
            onClick={() => setPaginaRegistros(page)}
          >
            {page}
          </Pagination.Item>
        ))}

        <Pagination.Next
          onClick={() => setPaginaRegistros(currentPage + 1)}
          disabled={currentPage === totalPages}
        />
        <Pagination.Last
          onClick={() => setPaginaRegistros(totalPages)}
          disabled={currentPage === totalPages}
        />
      </Pagination>
    );
  };

  return (
    <>
      {/* Overlay de exportación */}
      {exportando && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(255,255,255,0.7)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
          }}
        >
          <Spinner
            animation="border"
            variant="primary"
            style={{ width: 60, height: 60 }}
          />
          <div className="mt-3 fs-5 fw-bold text-primary">
            Procesando reporte, por favor espere...
          </div>
        </div>
      )}

      <Container fluid className="py-4">
        <Card
          className="shadow-lg border-0"
          style={{ borderRadius: "15px", overflow: "hidden", minHeight: "90vh" }}
        >
          {/* Header Premium con degradado verde oscuro */}
          <Card.Header
            className="text-white py-4 position-relative"
            style={{
              background: "linear-gradient(135deg, #0d4f3c 0%, #2c7a2c 100%)",
              borderBottom: "3px solid #f59e0b",
              borderRadius: "15px 15px 0 0",
            }}
          >
            <div
              className="position-absolute end-0 top-50 translate-middle-y me-4"
              style={{ zIndex: 10 }}
            >
              <BotonManualHeader />
            </div>
            <div className="text-center">
              <h2 className="fw-bold mb-1">
                <FaClipboardList className="me-3" />
                Reportes de Asistencia
              </h2>
              <p className="mb-0 opacity-75">
                Análisis completo de asistencia, puntualidad y rendimiento del equipo
              </p>
            </div>
          </Card.Header>

          <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
            {error && (
              <Alert variant="danger" dismissible onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {initialLoading ? (
              <div className="text-center py-5">
                <Spinner
                  animation="border"
                  variant="primary"
                  style={{ width: "3rem", height: "3rem" }}
                />
                <p className="mt-3 text-muted">Inicializando reporte...</p>
                <p className="text-muted small">
                  Esto puede tomar unos momentos la primera vez
                </p>
              </div>
            ) : (
              <>
                {/* Acciones Rápidas */}
                <Card
                  className="border-0 mb-4 shadow-sm"
                  style={{
                    background: "linear-gradient(135deg, #fffcea, #fff9db)",
                    borderRadius: 14,
                  }}
                >
                  <Card.Body className="p-3 d-flex justify-content-center gap-3 flex-wrap align-items-center">
                    <Button
                      variant="primary"
                      onClick={fetchData}
                      disabled={loading}
                      className="d-flex align-items-center gap-2"
                      style={{
                        borderRadius: "10px",
                        padding: "10px 20px",
                        fontWeight: "bold",
                      }}
                    >
                      {loading ? (
                        <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                      ) : (
                        <FaClipboardList />
                      )}
                      Buscar
                    </Button>
                    <Button
                      variant="success"
                      onClick={() => exportReport("excel")}
                      disabled={filteredAttendance.length === 0}
                      className="d-flex align-items-center gap-2"
                      style={{
                        borderRadius: "10px",
                        background: "linear-gradient(135deg, #28a745 0%, #20c997 100%)",
                        border: "none",
                        padding: "10px 20px",
                        fontWeight: "bold",
                      }}
                    >
                      <FaFileExcel size="18px" /> Excel
                    </Button>
                  </Card.Body>
                </Card>

                {/* Filtros */}
                <div className="bg-white p-4 rounded-4 shadow-sm mb-4 border text-dark">
                  <h5 className="mb-3 fw-bold d-flex align-items-center">
                    <FaFilter className="me-2 text-primary" /> Filtros de Reporte
                  </h5>
                  <Form>
                    <Row className="g-3 align-items-end">
                      <Col md={2}>
                        <Form.Group>
                          <Form.Label className="fw-bold small text-uppercase opacity-75">Período</Form.Label>
                          <Form.Control
                            type="month"
                            value={selectedMonth}
                            onChange={(e) => setSelectedMonth(e.target.value)}
                            style={{ borderRadius: "8px" }}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={2}>
                        <Form.Group>
                          <Form.Label className="fw-bold small text-uppercase opacity-75">DNI</Form.Label>
                          <div className="position-relative">
                            <Form.Control
                              type="text"
                              inputMode="numeric"
                              placeholder="Filtrar DNI..."
                              value={filtroDni}
                              onChange={(e) => setFiltroDni(e.target.value)}
                              style={{ borderRadius: "8px" }}
                            />
                            {dniBuscando && (
                              <Spinner
                                animation="border"
                                size="sm"
                                className="position-absolute top-50 end-0 translate-middle-y me-2 text-primary"
                              />
                            )}
                          </div>
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label className="fw-bold small text-uppercase opacity-75">Empleado</Form.Label>
                          <Form.Select
                            value={selectedEmployee}
                            onChange={(e) => setSelectedEmployee(e.target.value)}
                            style={{ borderRadius: "8px" }}
                          >
                            <option value="todos">
                              Todos los empleados{filtroDni.trim() ? ` (${empleadosSelector.length})` : ""}
                            </option>
                            {(grupoSeleccionado === "todos"
                              ? empleadosSelector
                              : empleadosSelector.filter((emp) =>
                                grupos
                                  .find((g) => g.id === grupoSeleccionado)
                                  ?.members.includes(emp.dni)
                              )
                            ).map((employee) => (
                              <option key={employee.dni} value={employee.dni}>
                                {employee.nombre} ({employee.dni})
                                {employee.inactivo ? " — inactivo" : ""}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={2}>
                        <Form.Group>
                          <Form.Label className="fw-bold small text-uppercase opacity-75">Grupo</Form.Label>
                          <Form.Select
                            value={grupoSeleccionado}
                            onChange={(e) => setGrupoSeleccionado(e.target.value)}
                            disabled={alcanceGrupo.restringidoPorGrupo && grupos.length <= 1}
                            style={{ borderRadius: "8px" }}
                          >
                            {!alcanceGrupo.restringidoPorGrupo && (
                              <option value="todos">Todos los grupos</option>
                            )}
                            {grupos.map((grupo) => (
                              <option key={grupo.id} value={grupo.id}>
                                {grupo.name}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group>
                          <Form.Check
                            type="switch"
                            id="incluir-inactivos"
                            label="Incluir trabajadores inactivos"
                            checked={incluirInactivos}
                            onChange={(e) => setIncluirInactivos(e.target.checked)}
                            className="fw-bold small text-uppercase opacity-75"
                          />
                          {incluirInactivos && inactivosLoading && (
                            <div className="d-flex align-items-center gap-2 small text-muted">
                              <Spinner animation="border" size="sm" />
                              Cargando inactivos...
                            </div>
                          )}
                        </Form.Group>
                      </Col>
                    </Row>
                  </Form>
                </div>

                {!isGlobalScopeRole(usuario?.rol) && gruposAsignadosUsuario.length > 0 && (
                  <Alert variant="info" className="mb-4 d-flex align-items-center rounded-3 border-0 shadow-sm">
                    <FaClipboardList className="me-2 text-primary" />
                    <strong>Grupos asignados a tu usuario:</strong>{" "}
                    {gruposAsignadosUsuario.map((g) => g.name).join(", ")}
                  </Alert>
                )}
                {loading ? (
                  <div className="text-center py-5">
                    <Spinner
                      animation="border"
                      variant="primary"
                      style={{ width: "3rem", height: "3rem" }}
                    />
                    <p className="mt-3 text-muted">
                      Cargando datos del reporte...
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Resumen de estadísticas solo asistencia */}
                    <Row className="mb-4">
                      <Col md={3}>
                        <Card className="h-100 border-primary">
                          <Card.Body className="text-center">
                            <h1 className="display-4 text-primary">
                              {statistics.totalEmployees}
                            </h1>
                            <p className="text-muted mb-0">Empleados</p>
                          </Card.Body>
                        </Card>
                      </Col>
                      <Col md={3}>
                        <Card className="h-100 border-success">
                          <Card.Body className="text-center">
                            <h1 className="display-4 text-success">
                              {statistics.attendanceRate.toFixed(1)}%
                            </h1>
                            <p className="text-muted mb-0">
                              Tasa de Asistencia
                              <br />
                              <span style={{ fontSize: "0.9em" }}>
                                Objetivo a la fecha:{" "}
                                {statistics.objetivoAFecha.toFixed(1)}%
                              </span>
                            </p>
                          </Card.Body>
                        </Card>
                      </Col>
                      <Col md={3}>
                        <Card className="h-100 border-info">
                          <Card.Body className="text-center">
                            <h1 className="display-4 text-info">
                              {(() => {
                                // ...cálculo de porcentaje de empleados con asistencia perfecta...
                                const [year, month] = selectedMonth.split("-");
                                const hoy = new Date();
                                const esMesActual =
                                  Number(year) === hoy.getFullYear() &&
                                  Number(month) === hoy.getMonth() + 1;
                                const lastDay = esMesActual
                                  ? hoy.getDate()
                                  : new Date(year, month, 0).getDate();

                                let laborablesHastaHoy = 0;
                                for (let d = 1; d <= lastDay; d++) {
                                  const date = new Date(year, month - 1, d);
                                  const dayOfWeek = date.getDay();
                                  if (dayOfWeek >= 1 && dayOfWeek <= 5)
                                    laborablesHastaHoy++;
                                }

                                const empleados = {};
                                filtrarPreferirRegularizacion(filteredAttendance).forEach((record) => {
                                  if (!record.dni || !record.fechaJornada)
                                    return;
                                  const fechaJornada =
                                    typeof record.fechaJornada === "string"
                                      ? record.fechaJornada
                                      : format(
                                        record.fechaJornada.toDate(),
                                        "yyyy-MM-dd"
                                      );
                                  const dia = Number(
                                    fechaJornada.split("-")[2]
                                  );
                                  if (
                                    fechaJornada.startsWith(
                                      `${year}-${month.padStart(2, "0")}`
                                    ) &&
                                    dia <= lastDay
                                  ) {
                                    if (!empleados[record.dni])
                                      empleados[record.dni] = {};
                                    if (!empleados[record.dni][fechaJornada])
                                      empleados[record.dni][fechaJornada] = {
                                        ingreso: false,
                                        salida: false,
                                      };
                                    if (record.tipo === "Ingreso")
                                      empleados[record.dni][
                                        fechaJornada
                                      ].ingreso = true;
                                    if (record.tipo === "Salida")
                                      empleados[record.dni][
                                        fechaJornada
                                      ].salida = true;
                                  }
                                });

                                // El % se calcula solo sobre trabajadores ACTIVOS:
                                // incluir cesados distorsionaría el indicador cuando
                                // el switch de inactivos está encendido
                                const empleadosParaKpi = employeeList.filter(
                                  (emp) => emp.activo === true
                                );
                                let perfectos = 0;
                                empleadosParaKpi.forEach((emp) => {
                                  let completos = 0;
                                  for (let d = 1; d <= lastDay; d++) {
                                    const date = new Date(year, month - 1, d);
                                    const dayOfWeek = date.getDay();
                                    if (dayOfWeek >= 1 && dayOfWeek <= 5) {
                                      const fecha = `${year}-${month.padStart(
                                        2,
                                        "0"
                                      )}-${d.toString().padStart(2, "0")}`;
                                      if (
                                        empleados[emp.dni] &&
                                        empleados[emp.dni][fecha] &&
                                        empleados[emp.dni][fecha].ingreso &&
                                        empleados[emp.dni][fecha].salida
                                      ) {
                                        completos++;
                                      }
                                    }
                                  }
                                  if (
                                    completos === laborablesHastaHoy &&
                                    laborablesHastaHoy > 0
                                  )
                                    perfectos++;
                                });

                                const porcentaje =
                                  empleadosParaKpi.length > 0
                                    ? (
                                      (perfectos / empleadosParaKpi.length) *
                                      100
                                    ).toFixed(1)
                                    : "0.0";
                                return `${porcentaje}%`;
                              })()}
                            </h1>
                            <p className="text-muted mb-0">
                              % Empleados con asistencia perfecta
                              <br />
                            </p>
                          </Card.Body>
                        </Card>
                      </Col>
                      <Col md={3}>
                        <Card className="h-100 border-warning">
                          <Card.Body className="text-center">
                            <h1 className="display-4 text-warning">
                              {
                                filteredAttendance.filter(
                                  (r) => r.tipo === "Ingreso"
                                ).length
                              }
                            </h1>
                            <p className="text-muted mb-0">
                              Marcaciones de Ingreso
                              <br />
                              <span style={{ fontSize: "0.9em" }}>
                                Objetivo hasta la fecha:{" "}
                                {(() => {
                                  const uniqueEmployees = new Set(
                                    filteredAttendance.map(
                                      (record) => record.dni
                                    )
                                  );
                                  const [year, month] =
                                    selectedMonth.split("-");
                                  const hoy = new Date();
                                  const esMesActual =
                                    Number(year) === hoy.getFullYear() &&
                                    Number(month) === hoy.getMonth() + 1;
                                  let laborablesHastaHoy = 0;
                                  const daysInMonth = new Date(
                                    year,
                                    month,
                                    0
                                  ).getDate();
                                  const lastDay = esMesActual
                                    ? hoy.getDate()
                                    : daysInMonth;
                                  for (let d = 1; d <= lastDay; d++) {
                                    const date = new Date(year, month - 1, d);
                                    const dayOfWeek = date.getDay();
                                    if (dayOfWeek >= 1 && dayOfWeek <= 5)
                                      laborablesHastaHoy++;
                                  }
                                  const objetivo =
                                    uniqueEmployees.size * laborablesHastaHoy;
                                  return objetivo;
                                })()}
                              </span>
                            </p>
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>

                    {/* Tabs solo de asistencia */}
                    <Tabs
                      activeKey={tabActivo}
                      onSelect={(k) => setTabActivo(k)}
                      className="mb-4"
                    >
                      <Tab eventKey="resumen" title="Resumen">
                        <Row className="mt-4">
                          <Col md={12}>
                            <Card
                              style={{ minHeight: "600px" }}
                              className="h-100"
                            >
                              <Card.Header>Asistencia Diaria</Card.Header>
                              <Card.Body>
                                <div
                                  style={{
                                    height: "100px",
                                    width: "100%",
                                    position: "relative",
                                  }}
                                >
                                  <Line
                                    data={getDailyAttendanceData()}
                                    options={lineChartOptions}
                                  />
                                </div>
                              </Card.Body>
                            </Card>
                          </Col>
                        </Row>
                      </Tab>
                      <Tab eventKey="detalles" title="Detalles">
                        <Row className="mt-4">
                          <Col md={12}>
                            <Card>
                              <Card.Header className="d-flex justify-content-between align-items-center">
                                <div>Marcaciones Diarias por Empleado</div>
                                <div>
                                  <Button
                                    variant="outline-success"
                                    className="me-2"
                                    size="sm"
                                    onClick={() => exportReport("excel")}
                                  >
                                    <FaFileExcel className="me-1" /> Excel
                                  </Button>
                                </div>
                              </Card.Header>
                              <Card.Body>
                                {filteredAttendance.length === 0 ? (
                                  <p className="text-center text-muted">
                                    No hay datos para mostrar en este período.
                                  </p>
                                ) : (
                                  <div className="table-responsive">
                                    <table className="table table-bordered table-hover table-sm">
                                      <thead className="table-light">
                                        <tr>
                                          <th>Empleado</th>
                                          {Array.from(
                                            {
                                              length: new Date(
                                                selectedMonth.split("-")[0],
                                                selectedMonth.split("-")[1],
                                                0
                                              ).getDate(),
                                            },
                                            (_, i) => i + 1
                                          ).map((day) => (
                                            <th
                                              key={day}
                                              className="text-center"
                                            >
                                              {day}
                                            </th>
                                          ))}
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {processAttendanceForTable().map(
                                          (employee) => (
                                            <tr key={employee.dni}>
                                              <td>{employee.nombre}</td>
                                              {Object.keys(employee.dias).map(
                                                (day) => {
                                                  const dayData =
                                                    employee.dias[day];
                                                  let bgColor = "bg-light";
                                                  let content = "—";

                                                  const totalMarcaciones = [
                                                    dayData.ingreso,
                                                    dayData.refrigerioInicio,
                                                    dayData.refrigerioFin,
                                                    dayData.salida
                                                  ].filter(Boolean).length;

                                                  if (totalMarcaciones === 4) {
                                                    bgColor =
                                                      "bg-success bg-opacity-10";
                                                    content = "✓✓✓✓";
                                                  } else if (totalMarcaciones > 0) {
                                                    bgColor =
                                                      "bg-warning bg-opacity-10";
                                                    content = "✓".repeat(totalMarcaciones);
                                                  }

                                                  return (
                                                    <td
                                                      key={day}
                                                      className={`text-center ${bgColor}`}
                                                      title={`Ingreso: ${dayData.ingreso || "-"}, Ref. Inicio: ${dayData.refrigerioInicio || "-"}, Ref. Fin: ${dayData.refrigerioFin || "-"}, Salida: ${dayData.salida || "-"}`}
                                                    >
                                                      {content}
                                                    </td>
                                                  );
                                                }
                                              )}
                                            </tr>
                                          )
                                        )}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                              </Card.Body>
                            </Card>
                          </Col>
                        </Row>
                      </Tab>
                      <Tab eventKey="detallado" title="Detalle de Horas">
                        <Row className="mt-4">
                          <Col md={12}>
                            <Card>
                              <Card.Header className="d-flex justify-content-between align-items-center">
                                <div>Horarios de Ingreso y Salida</div>
                                <div>
                                  <Button
                                    variant="outline-success"
                                    className="me-2"
                                    size="sm"
                                    onClick={() => exportReport("excel")}
                                  >
                                    <FaFileExcel className="me-1" /> Excel
                                  </Button>
                                </div>
                              </Card.Header>
                              <Card.Body>
                                {filteredAttendance.length === 0 ? (
                                  <p className="text-center text-muted">
                                    No hay datos para mostrar en este período.
                                  </p>
                                ) : (
                                  <>
                                    <div className="table-responsive">
                                      <table className="table table-bordered table-hover table-sm">
                                        <thead className="table-light">
                                          <tr>
                                            <th>Empleado</th>
                                            <th>Centro</th>
                                            <th>Día</th>
                                            <th>Inicio Refrigerio</th>
                                            <th>Fin Refrigerio</th>
                                            <th>Horas Refrigerio</th>
                                            <th>Ingreso</th>
                                            <th>Salida</th>
                                            <th>Horas Trabajadas</th>
                                            <th>Estado</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {getDetalleHorasPaginado().items.map(
                                            (item) => {
                                              const {
                                                employeeNombre,
                                                employeeDni,
                                                day,
                                                dayData,
                                              } = item;

                                              // Calcular horas de refrigerio
                                              let horasRefrigerio = null;
                                              if (
                                                dayData.refrigerioInicio &&
                                                dayData.refrigerioFin
                                              ) {
                                                horasRefrigerio =
                                                  calcularHorasTrabajadas(
                                                    dayData.refrigerioInicio,
                                                    dayData.refrigerioFin
                                                  );
                                              }

                                              // Calcular horas trabajadas descontando refrigerio
                                              let horasTrabajadas =
                                                calcularHorasTrabajadas(
                                                  dayData.ingreso,
                                                  dayData.salida
                                                );
                                              if (
                                                horasTrabajadas &&
                                                horasRefrigerio
                                              ) {
                                                const [hT, mT] = horasTrabajadas
                                                  .split(":")
                                                  .map(Number);
                                                const [hR, mR] = horasRefrigerio
                                                  .split(":")
                                                  .map(Number);
                                                let totalMin =
                                                  hT * 60 + mT - (hR * 60 + mR);
                                                if (totalMin < 0) totalMin = 0;
                                                const hFinal = Math.floor(
                                                  totalMin / 60
                                                );
                                                const mFinal = totalMin % 60;
                                                horasTrabajadas = `${hFinal
                                                  .toString()
                                                  .padStart(2, "0")}:${mFinal
                                                    .toString()
                                                    .padStart(2, "0")}`;
                                              }

                                              // Estado
                                              let estado = "No marcó";
                                              let badgeClass = "bg-secondary";
                                              if (
                                                dayData.ingreso &&
                                                dayData.salida
                                              ) {
                                                estado = "Completo";
                                                badgeClass = "bg-success";
                                              } else if (dayData.ingreso) {
                                                estado = "Solo ingreso";
                                                badgeClass = "bg-warning";
                                              } else if (dayData.salida) {
                                                estado = "Solo salida";
                                                badgeClass = "bg-warning";
                                              }

                                              // Render editable cell por campo (clave única por celda)
                                              const renderEditableCell = (
                                                tipo,
                                                valor,
                                                ingresoValor // <-- Nuevo parámetro para saber si hay ingreso
                                              ) => {
                                                const cellKey = `${employeeDni}-${day}-${tipo}`;
                                                const handleEditClick = () => {
                                                  // No permitir edición si no hay un valor existente
                                                  if (!valor) {
                                                    return;
                                                  }

                                                  let tipoLegible = tipo;
                                                  switch (tipo) {
                                                    case "ingreso":
                                                      tipoLegible = "ingreso";
                                                      break;
                                                    case "salida":
                                                      tipoLegible = "salida";
                                                      break;
                                                    case "refrigerioInicio":
                                                      tipoLegible =
                                                        "inicio de refrigerio";
                                                      break;
                                                    case "refrigerioFin":
                                                      tipoLegible =
                                                        "fin de refrigerio";
                                                      break;
                                                    default:
                                                      tipoLegible = "marcación";
                                                  }

                                                  Swal.fire({
                                                    title: "Confirmar Edición",
                                                    text: `Se va a editar la hora de ${tipoLegible}. ¿Deseas continuar?`,
                                                    icon: "question",
                                                    showCancelButton: true,
                                                    confirmButtonText:
                                                      "Sí, editar",
                                                    cancelButtonText:
                                                      "Cancelar",
                                                  }).then((result) => {
                                                    if (result.isConfirmed) {
                                                      setEditando({
                                                        [cellKey]: valor ?? "",
                                                      });
                                                    }
                                                  });
                                                };
                                                // Permitir eliminar solo si es refrigerio y NO hay ingreso
                                                const puedeEliminar =
                                                  (tipo ===
                                                    "refrigerioInicio" ||
                                                    tipo === "refrigerioFin") &&
                                                  !ingresoValor &&
                                                  valor;
                                                return editando[cellKey] !==
                                                  undefined ? (
                                                  <Form.Control
                                                    type="text"
                                                    size="sm"
                                                    value={
                                                      editando[cellKey] ?? ""
                                                    }
                                                    placeholder="hh:mm:ss"
                                                    onChange={(e) =>
                                                      setEditando((prev) => ({
                                                        ...prev,
                                                        [cellKey]:
                                                          e.target.value,
                                                      }))
                                                    }
                                                    onBlur={() => {
                                                      // Solo guardar si el valor cambió
                                                      if (
                                                        (editando[cellKey] ??
                                                          "") !== (valor ?? "")
                                                      ) {
                                                        guardarEdicionMarcacion(
                                                          employeeDni,
                                                          day,
                                                          tipo,
                                                          editando[cellKey] ??
                                                          ""
                                                        );
                                                      } else {
                                                        setEditando((prev) => {
                                                          const nuevo = {
                                                            ...prev,
                                                          };
                                                          delete nuevo[cellKey];
                                                          return nuevo;
                                                        });
                                                      }
                                                    }}
                                                    autoFocus
                                                  />
                                                ) : (
                                                  <span
                                                    style={{
                                                      cursor: "pointer",
                                                      color: "#0d6efd",
                                                      display: "inline-flex",
                                                      alignItems: "center",
                                                      gap: "0.5rem",
                                                    }}
                                                    title={`Editar hora de ${tipo}`}
                                                    onClick={handleEditClick}
                                                  >
                                                    {valor || "—"}
                                                    {item.dayData[`${tipo}Modificado`] && (
                                                      <span className="text-danger fw-bold" title="Editado manualmente" style={{ fontSize: '0.8rem' }}>(*)</span>
                                                    )}
                                                    {puedeEliminar && (
                                                      <FaTrash
                                                        className="text-danger"
                                                        title="Eliminar registro de refrigerio"
                                                        style={{
                                                          cursor: "pointer",
                                                        }}
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          eliminarMarcacionRefrigerio(
                                                            employeeDni,
                                                            day,
                                                            tipo
                                                          );
                                                        }}
                                                      />
                                                    )}
                                                  </span>
                                                );
                                              };

                                              // Función para eliminar registro de refrigerio
                                              const eliminarMarcacionRefrigerio =
                                                async (dni, day, tipo) => {
                                                  const tipoFirestore =
                                                    tipo === "refrigerioInicio"
                                                      ? [
                                                        "Refrigerio-Inicio",
                                                        "Refrigerio_Inicio",
                                                      ]
                                                      : [
                                                        "Refrigerio-Fin",
                                                        "Refrigerio_Fin",
                                                      ];

                                                  try {
                                                    setLoading(true);
                                                    const registrosRef =
                                                      collection(
                                                        db,
                                                        `asistencia/${dni}/registros`
                                                      );
                                                    const q = query(
                                                      registrosRef,
                                                      where(
                                                        "fechaJornada",
                                                        "==",
                                                        day
                                                      ),
                                                      where(
                                                        "tipo",
                                                        "in",
                                                        tipoFirestore
                                                      )
                                                    );
                                                    const snap = await getDocs(
                                                      q
                                                    );

                                                    if (!snap.empty) {
                                                      const result =
                                                        await Swal.fire({
                                                          title:
                                                            "¿Eliminar registro de refrigerio?",
                                                          text: "Esta acción no se puede deshacer.",
                                                          icon: "warning",
                                                          showCancelButton: true,
                                                          confirmButtonText:
                                                            "Sí, eliminar",
                                                          cancelButtonText:
                                                            "Cancelar",
                                                        });
                                                      if (!result.isConfirmed) {
                                                        setLoading(false);
                                                        return;
                                                      }

                                                      for (const docu of snap.docs) {
                                                        await deleteDoc(
                                                          doc(
                                                            db,
                                                            `asistencia/${dni}/registros`,
                                                            docu.id
                                                          )
                                                        );
                                                      }

                                                      setAttendanceData(
                                                        (prevData) =>
                                                          prevData.filter(
                                                            (record) =>
                                                              !(
                                                                record.dni ===
                                                                dni &&
                                                                ((typeof record.fechaJornada ===
                                                                  "string" &&
                                                                  record.fechaJornada ===
                                                                  day) ||
                                                                  (record.fechaJornada &&
                                                                    typeof record.fechaJornada !==
                                                                    "string" &&
                                                                    record
                                                                      .fechaJornada
                                                                      .toDate &&
                                                                    format(
                                                                      record.fechaJornada.toDate(),
                                                                      "yyyy-MM-dd"
                                                                    ) ===
                                                                    day)) &&
                                                                tipoFirestore.includes(
                                                                  record.tipo
                                                                )
                                                              )
                                                          )
                                                      );

                                                      Swal.fire(
                                                        "Eliminado",
                                                        "El registro fue eliminado.",
                                                        "success"
                                                      );
                                                    }
                                                  } catch (e) {
                                                    console.error(
                                                      "Error al eliminar marcación:",
                                                      e
                                                    );
                                                    Swal.fire(
                                                      "Error",
                                                      "No se pudo eliminar el registro",
                                                      "error"
                                                    );
                                                  } finally {
                                                    setLoading(false);
                                                  }
                                                };

                                              return (
                                                <tr
                                                  key={`${employeeDni}-${day}`}
                                                >
                                                  <td>{employeeNombre}</td>
                                                  <td>
                                                    {dayData.centroNombre ||
                                                      "—"}
                                                  </td>
                                                  <td>{day}</td>
                                                  <td>
                                                    {renderEditableCell(
                                                      "refrigerioInicio",
                                                      dayData.refrigerioInicio,
                                                      dayData.ingreso
                                                    )}
                                                  </td>
                                                  <td>
                                                    {renderEditableCell(
                                                      "refrigerioFin",
                                                      dayData.refrigerioFin,
                                                      dayData.ingreso
                                                    )}
                                                  </td>
                                                  <td>
                                                    {horasRefrigerio || "—"}
                                                  </td>
                                                  <td>
                                                    {renderEditableCell(
                                                      "ingreso",
                                                      dayData.ingreso
                                                    )}
                                                  </td>
                                                  <td>
                                                    {renderEditableCell(
                                                      "salida",
                                                      dayData.salida
                                                    )}
                                                  </td>
                                                  <td>
                                                    {horasTrabajadas || "—"}
                                                  </td>
                                                  <td>
                                                    <span
                                                      className={`badge ${badgeClass}`}
                                                    >
                                                      {estado}
                                                    </span>
                                                  </td>
                                                </tr>
                                              );
                                            }
                                          )}
                                        </tbody>
                                      </table>
                                    </div>
                                    {/* Paginación y controles para Detalle de Horas */}
                                    <div className="d-flex justify-content-between align-items-center mt-3">
                                      <div>
                                        <Form.Select
                                          size="sm"
                                          style={{ width: "100px" }}
                                          value={elementosPorPagina}
                                          onChange={(e) => {
                                            setElementosPorPagina(
                                              Number(e.target.value)
                                            );
                                            setPaginaActual(1);
                                          }}
                                        >
                                          <option value={10}>10 filas</option>
                                          <option value={25}>25 filas</option>
                                          <option value={50}>50 filas</option>
                                          <option value={100}>100 filas</option>
                                        </Form.Select>
                                      </div>
                                      <div className="small text-muted">
                                        Mostrando{" "}
                                        {getDetalleHorasPaginado().startIndex +
                                          1}{" "}
                                        a {getDetalleHorasPaginado().endIndex}{" "}
                                        de{" "}
                                        {getDetalleHorasPaginado().totalItems}{" "}
                                        registros
                                      </div>
                                      {renderPagination(
                                        getDetalleHorasPaginado()
                                      )}
                                    </div>
                                  </>
                                )}
                              </Card.Body>
                            </Card>
                          </Col>
                        </Row>
                      </Tab>
                      <Tab
                        eventKey="resumenEmpleados"
                        title="Resumen por Empleado"
                      >
                        <Row className="mt-4">
                          <Col md={12}>
                            <Card>
                              <Card.Header className="d-flex justify-content-between align-items-center">
                                <div>Resumen por Empleado</div>
                                <div>
                                  <Button
                                    variant="outline-success"
                                    className="me-2"
                                    size="sm"
                                    onClick={() => exportReport("excel")}
                                  >
                                    <FaFileExcel className="me-1" /> Excel
                                  </Button>
                                </div>
                              </Card.Header>
                              <Card.Body>
                                {filteredAttendance.length === 0 ? (
                                  <p className="text-center text-muted">
                                    No hay datos para mostrar en este período.
                                  </p>
                                ) : (
                                  <div className="table-responsive">
                                    <table className="table table-bordered table-hover">
                                      <thead className="table-light">
                                        <tr>
                                          <th>Empleado</th>
                                          <th className="text-center">
                                            Días Trabajados
                                          </th>
                                          <th className="text-center">
                                            Días Completos
                                          </th>
                                          <th className="text-center">
                                            Solo Entradas
                                          </th>
                                          <th className="text-center">
                                            Solo Salidas
                                          </th>
                                          <th className="text-center">
                                            Horas Totales
                                          </th>
                                          <th className="text-center">
                                            Promedio Diario
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {processAttendanceForTable().map(
                                          (employee) => {
                                            // Calcular estadísticas por empleado
                                            const diasCompletos = Object.values(
                                              employee.dias
                                            ).filter(
                                              (d) => d.ingreso && d.salida
                                            ).length;
                                            const soloEntradas = Object.values(
                                              employee.dias
                                            ).filter(
                                              (d) => d.ingreso && !d.salida
                                            ).length;
                                            const soloSalidas = Object.values(
                                              employee.dias
                                            ).filter(
                                              (d) => !d.ingreso && d.salida
                                            ).length;

                                            // Calcular llegadas tarde
                                            const llegadasTarde = Object.values(
                                              employee.dias
                                            ).filter((d) => {
                                              if (!d.ingreso) return false;
                                              const [hour, minute] = d.ingreso
                                                .split(":")
                                                .map(Number);
                                              return (
                                                hour > startHour ||
                                                (hour === startHour &&
                                                  minute > startMinute)
                                              );
                                            }).length;

                                            // Calcular horas totales trabajadas
                                            let horasTotales = 0;
                                            let minutosTotales = 0;
                                            Object.values(
                                              employee.dias
                                            ).forEach((d) => {
                                              if (d.ingreso && d.salida) {
                                                const horasTrabajadas =
                                                  calcularHorasTrabajadas(
                                                    d.ingreso,
                                                    d.salida
                                                  );
                                                if (horasTrabajadas) {
                                                  const [horas, minutos] =
                                                    horasTrabajadas
                                                      .split(":")
                                                      .map(Number);
                                                  horasTotales += horas;
                                                  minutosTotales += minutos;
                                                }
                                              }
                                            });

                                            // Ajustar si los minutos son más de 60
                                            horasTotales += Math.floor(
                                              minutosTotales / 60
                                            );
                                            minutosTotales =
                                              minutosTotales % 60;

                                            const horasTotalesStr = `${horasTotales}:${minutosTotales
                                              .toString()
                                              .padStart(2, "0")}`;
                                            const promedioDiario =
                                              diasCompletos > 0
                                                ? `${(
                                                  horasTotales / diasCompletos
                                                ).toFixed(1)}h`
                                                : "—";

                                            return (
                                              <tr key={employee.dni}>
                                                <td>{employee.nombre}</td>
                                                <td className="text-center">
                                                  {diasCompletos +
                                                    soloEntradas +
                                                    soloSalidas}
                                                </td>
                                                <td className="text-center">
                                                  {diasCompletos}
                                                </td>
                                                <td className="text-center">
                                                  {soloEntradas}
                                                </td>
                                                <td className="text-center">
                                                  {soloSalidas}
                                                </td>
                                                <td className="text-center fw-bold">
                                                  {horasTotalesStr}
                                                </td>
                                                <td className="text-center">
                                                  {promedioDiario}
                                                </td>
                                              </tr>
                                            );
                                          }
                                        )}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                              </Card.Body>
                            </Card>
                          </Col>
                        </Row>
                      </Tab>
                      <Tab eventKey="registros" title="Registros">
                        <Row className="mt-4">
                          <Col md={12}>
                            <Card>
                              <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2 py-3">
                                <div className="fw-bold text-dark d-flex align-items-center">
                                  <FaClipboardList className="me-2 text-primary" />
                                  Registro completo de marcaciones
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => setShowModalColumnas(true)}
                                    title="Personalizar visibilidad y orden de las columnas"
                                  >
                                    <FaCog className="me-1" /> Personalizar Columnas
                                  </Button>
                                  <Button
                                    variant="outline-success"
                                    size="sm"
                                    onClick={() => exportReport("excel")}
                                  >
                                    <FaFileExcel className="me-1" /> Excel
                                  </Button>
                                </div>
                              </Card.Header>
                              <Card.Body>
                                {filteredAttendance.length === 0 ? (
                                  <p className="text-center text-muted py-4">
                                    No hay registros para mostrar en este período.
                                  </p>
                                ) : (
                                  (() => {
                                    const registrosPaginados = getRegistrosPaginados();
                                    const columnasVisibles = columnasRegistrosConfig.filter((c) => c.visible);

                                    return (
                                      <>
                                    <Row className="g-2 mb-3">
                                      <Col md={6}>
                                        <Form.Control
                                          type="text"
                                          placeholder="Buscar por DNI, nombre, cargo, tipo, origen..."
                                          value={busquedaRegistro}
                                          onChange={(e) => setBusquedaRegistro(e.target.value)}
                                        />
                                      </Col>
                                      <Col md={3}>
                                        <Form.Select
                                          value={filtroTipoRegistro}
                                          onChange={(e) => setFiltroTipoRegistro(e.target.value)}
                                        >
                                          <option value="todos">Todos los tipos</option>
                                          <option value="Ingreso">Ingreso</option>
                                          <option value="Salida">Salida</option>
                                          <option value="Refrigerio-Inicio">Refrigerio-Inicio</option>
                                          <option value="Refrigerio_Inicio">Refrigerio_Inicio</option>
                                          <option value="Refrigerio-Fin">Refrigerio-Fin</option>
                                          <option value="Refrigerio_Fin">Refrigerio_Fin</option>
                                        </Form.Select>
                                      </Col>
                                      <Col md={3}>
                                        <Form.Select
                                          value={elementosPorPaginaRegistros}
                                          onChange={(e) => {
                                            setElementosPorPaginaRegistros(Number(e.target.value));
                                            setPaginaRegistros(1);
                                          }}
                                        >
                                          <option value={25}>25 filas</option>
                                          <option value={50}>50 filas</option>
                                          <option value={100}>100 filas</option>
                                          <option value={200}>200 filas</option>
                                        </Form.Select>
                                      </Col>
                                    </Row>

                                    <Row className="mb-3 align-items-center">
                                      <Col md={8}>
                                        <Form.Check
                                          type="switch"
                                          id="vista-avanzada-registros"
                                          label="Vista avanzada (activa todas las columnas técnicas y geolocalización)"
                                          checked={vistaAvanzadaRegistros}
                                          onChange={(e) => {
                                            const checked = e.target.checked;
                                            setVistaAvanzadaRegistros(checked);
                                            aplicarPreajusteColumnas(checked ? "avanzada" : "basica");
                                          }}
                                        />
                                      </Col>
                                      <Col md={4} className="text-end">
                                        <small className="text-muted">
                                          Columnas visibles: <strong>{columnasVisibles.length}</strong> de {columnasRegistrosConfig.length}
                                        </small>
                                      </Col>
                                    </Row>

                                    <div className="table-responsive">
                                      <table className="table table-bordered table-hover table-sm align-middle">
                                        <thead className="table-light">
                                          <tr>
                                            {columnasVisibles.map((col) => (
                                              <th key={col.id}>{col.label}</th>
                                            ))}
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {registrosPaginados.items.map((r) => (
                                            <tr key={r.id}>
                                              {columnasVisibles.map((col) => (
                                                <td key={col.id}>
                                                  {col.getValue(r, formatearFechaRegistro)}
                                                </td>
                                              ))}
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>

                                    <div className="d-flex justify-content-between align-items-center mt-3">
                                      <div className="small text-muted">
                                        Mostrando {registrosPaginados.totalItems === 0 ? 0 : registrosPaginados.startIndex + 1}
                                        {" "}a {registrosPaginados.endIndex} de {registrosPaginados.totalItems} registros
                                      </div>
                                      {renderPaginationRegistros(registrosPaginados)}
                                    </div>
                                      </>
                                    );
                                  })()
                                )}
                              </Card.Body>
                            </Card>
                          </Col>
                        </Row>
                      </Tab>
                    </Tabs>
                  </>
                )}
              </>
            )}
          </Card.Body>
        </Card>
      </Container>

      {/* Modal para Personalizar y Reordenar Columnas */}
      <Modal
        show={showModalColumnas}
        onHide={() => setShowModalColumnas(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton className="bg-light">
          <Modal.Title className="h6 fw-bold mb-0">
            <FaCog className="me-2 text-primary" />
            Personalizar y Reordenar Columnas del Reporte de Registros
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
          <p className="text-muted small mb-3">
            Selecciona las columnas que deseas incluir u ocultar en el reporte y ajusta su orden de aparición utilizando las flechas (▲/▼). Tu configuración se guardará automáticamente.
          </p>

          <Row className="g-2 mb-3 align-items-center">
            <Col md={7} className="d-flex flex-wrap gap-1 align-items-center">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => aplicarPreajusteColumnas("basica")}
                title="Mostrar solo las columnas principales por defecto"
              >
                Vista Básica
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => aplicarPreajusteColumnas("avanzada")}
                title="Mostrar todas las columnas incluyendo técnicas y geolocalización"
              >
                Vista Avanzada
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => aplicarPreajusteColumnas("todas")}
              >
                <FaCheckSquare className="me-1" /> Seleccionar Todas
              </Button>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => aplicarPreajusteColumnas("default")}
              >
                <FaUndo className="me-1" /> Restablecer
              </Button>
            </Col>
            <Col md={5}>
              <div className="input-group input-group-sm">
                <span className="input-group-text bg-white">
                  <FaSearch className="text-muted" size={12} />
                </span>
                <Form.Control
                  type="text"
                  placeholder="Buscar columna..."
                  value={busquedaColumnaModal}
                  onChange={(e) => setBusquedaColumnaModal(e.target.value)}
                />
              </div>
            </Col>
          </Row>

          <div className="border rounded p-2 bg-light">
            <div className="d-flex justify-content-between align-items-center px-3 py-2 border-bottom text-muted small fw-bold">
              <span>Visibilidad y Nombre de Columna</span>
              <span>Reordenar Posición</span>
            </div>

            <div
              className="list-group list-group-flush bg-white rounded mt-1"
              style={{ maxHeight: "380px", overflowY: "auto" }}
            >
              {columnasRegistrosConfig
                .map((col, index) => ({ col, index }))
                .filter(({ col }) =>
                  !busquedaColumnaModal.trim() ||
                  col.label.toLowerCase().includes(busquedaColumnaModal.trim().toLowerCase())
                )
                .map(({ col, index }) => (
                  <div
                    key={col.id}
                    className={`list-group-item d-flex align-items-center justify-content-between py-2 px-3 ${
                      col.visible ? "bg-white" : "bg-light text-muted"
                    }`}
                  >
                    <div className="d-flex align-items-center gap-2 flex-grow-1">
                      <Form.Check
                        type="switch"
                        id={`col-switch-${col.id}`}
                        checked={col.visible}
                        onChange={() => toggleColumna(col.id)}
                        label={
                          <span
                            className={
                              col.visible
                                ? "fw-semibold text-dark"
                                : "text-muted text-decoration-line-through"
                            }
                          >
                            {col.label}
                          </span>
                        }
                      />
                    </div>

                    <div className="d-flex align-items-center gap-1">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        className="py-0 px-2"
                        disabled={index === 0}
                        onClick={() => moverColumna(index, -1)}
                        title="Mover hacia arriba"
                      >
                        <FaArrowUp size={11} />
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        className="py-0 px-2"
                        disabled={index === columnasRegistrosConfig.length - 1}
                        onClick={() => moverColumna(index, 1)}
                        title="Mover hacia abajo"
                      >
                        <FaArrowDown size={11} />
                      </Button>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="bg-light py-2">
          <small className="text-muted me-auto">
            Visibles: <strong>{columnasRegistrosConfig.filter((c) => c.visible).length}</strong> de {columnasRegistrosConfig.length} columnas
          </small>
          <Button
            variant="primary"
            size="sm"
            onClick={() => setShowModalColumnas(false)}
          >
            Guardar y Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ReportesAA;
